import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import SignaturePad from 'react-signature-canvas';
import ReactDatePicker from 'react-datepicker';
import Select from 'react-select';
import { ToastContainer, toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import ClientItemsService from '../../Services/clientItems.service.js';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { styledInput } from '../../Utils/styledInputHelper.js';
import './CreateInvoice.css';
import ItemPricePickerDialog from '../../Components/ClientItems/SubComponents/ItemPricePickerDialog';
import { resolveItemPriceSelection } from '../../helpers/clientItemPrices';
import { shouldIncludeClientAccountCard } from '../../helpers/clientAccountCards';
import {
    Modal,
    TextField,
    CircularProgress,
    RadioGroup,
    FormControlLabel,
    Radio,
    Popper,
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Button,
    Autocomplete,
    Typography,
    Box,
    Card,
    CardContent,
    Divider,
    Chip,
    Alert,
    Stepper,
    Step,
    StepLabel,
    Grid,
    Stack,
    Avatar,
    Badge,
    Tooltip,
    Fade,
    Zoom,
    Slide,
    Collapse,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Switch,
    FormControlLabel as MuiFormControlLabel,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

import {
    styled,
    createTheme,
    ThemeProvider,
    alpha,
} from '@mui/material/styles';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Info as InfoIcon,
    ExpandMore as ExpandMoreIcon,
    ChevronRight as ChevronRightIcon,
    ChevronLeft as ChevronLeftIcon,
    AccountCircle as AccountCircleIcon,
    Business as BusinessIcon,
    Receipt as ReceiptIcon,
    Payment as PaymentIcon,
    AttachMoney as AttachMoneyIcon,
    CreditCard as CreditCardIcon,
    AccountBalance as AccountBalanceIcon,
    LocalAtm as LocalAtmIcon,
    Calculate as CalculateIcon,
} from '@mui/icons-material';
import EnumsService from '../../Services/enums.service.js';
import PuffLoader from "react-spinners/PuffLoader";
import UserService from '../../Services/user.service.js';
import AutocompleteSelect from '../../Components/Widgets/AutocompleteSelect/AutocompleteSelect.jsx';
import cancelIcon from '../../assests/images/Icons/cancelIcon.svg';
import SignatureReferenceModal from '../../Components/Widgets/SignatureReferenceModal/SignatureReferenceModal.jsx';
import TaxAuthorityService from '../../Services/taxAuthority.service.js';
import DocumentService from '../../Services/documents.service.js';
import { GetDocumentSettingsByCaseId } from '../../Services/documentSettings.service.js';
import 'react-toastify/dist/ReactToastify.css'; // Import Toastify CSS
import Constants, { ForeignCurrencyCode } from '../../assests/Constants/constants.js';
import CustomDatePicker from '../../Components/Widgets/CustomDates/CustomDatePicker/CustomDatePicker.js';
import { beautifyNumber } from '../../Utils/FormatNumber.js';
import ClientService from '../../Services/client.service.js';
import { calculateVatAmount, getVatValue } from '../../Utils/GetVatValue.js';
import { getIsraelInvoiceAmountLimit } from '../../Utils/GetIsraelInvoiceAmountLimit.js';
import { validateEmail, validatePhoneNumber, isValidShaamCustomerVatNumber } from '../../Utils/helpers.js';
import TaxAuthorityErrorModal from '../../Components/TaxAuthorityErrorModal/TaxAuthorityErrorModal.jsx';
import { useHamburgerMenu } from '../../Contexts/HamburgerMenuContext.js';
import Swal from 'sweetalert2';
import ForeignCurrencyService from '../../Services/foreignCurrency.service.js';
import { useClientNavigation } from '../../hooks/useClientNavigation.js';
import ProjectsService from '../../Services/projects.service.js';
import InventoryService from '../../Services/inventory.service.js';
import { useRequireDocumentTypeAccess } from '../../hooks/useRequireDocumentTypeAccess.js';
import InactiveClientAlert from '../../Components/InactiveClientAlert/InactiveClientAlert';
import EnhancedCreateInvoice from './SubComponent/EnhancedCreateInvoice.jsx';
import { useDocumentLinking, ENTITY_TYPE, pendingLinksToLinkedDescription, toLinkDraftItems, CURRENCY_ILS } from '../../Components/TransactionDocuments/documentLifecycle.js';

// Enhanced Theme
const enhancedTheme = createTheme({
    palette: {
        primary: {
            main: '#304FFF',
            light: '#5C7CFF',
            dark: '#1E40FF',
            contrastText: '#FFFFFF',
        },
        secondary: {
            main: '#F9AA2A',
            light: '#FFB74D',
            dark: '#F57C00',
            contrastText: '#000000',
        },
        background: {
            default: '#F8FAFC',
            paper: '#FFFFFF',
        },
        text: {
            primary: '#1A202C',
            secondary: '#4A5568',
        },
        error: {
            main: '#E53E3E',
            light: '#FC8181',
        },
        success: {
            main: '#38A169',
            light: '#68D391',
        },
        warning: {
            main: '#D69E2E',
            light: '#F6E05E',
        },
        info: {
            main: '#3182CE',
            light: '#63B3ED',
        },
    },
    typography: {
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        h1: {
            fontSize: '2.5rem',
            fontWeight: 700,
            lineHeight: 1.2,
        },
        h2: {
            fontSize: '2rem',
            fontWeight: 600,
            lineHeight: 1.3,
        },
        h3: {
            fontSize: '1.5rem',
            fontWeight: 600,
            lineHeight: 1.4,
        },
        h4: {
            fontSize: '1.25rem',
            fontWeight: 600,
            lineHeight: 1.4,
        },
        h5: {
            fontSize: '1.125rem',
            fontWeight: 600,
            lineHeight: 1.4,
        },
        h6: {
            fontSize: '1rem',
            fontWeight: 600,
            lineHeight: 1.4,
        },
        body1: {
            fontSize: '1rem',
            lineHeight: 1.6,
        },
        body2: {
            fontSize: '0.875rem',
            lineHeight: 1.6,
        },
        button: {
            textTransform: 'none',
            fontWeight: 600,
        },
    },
    shape: {
        borderRadius: 12,
    },
    shadows: [
        'none',
        '0px 2px 4px rgba(0, 0, 0, 0.05)',
        '0px 4px 8px rgba(0, 0, 0, 0.1)',
        '0px 8px 16px rgba(0, 0, 0, 0.1)',
        '0px 16px 32px rgba(0, 0, 0, 0.1)',
        '0px 32px 64px rgba(0, 0, 0, 0.1)',
        '0px 2px 4px rgba(0, 0, 0, 0.05)',
        '0px 4px 8px rgba(0, 0, 0, 0.1)',
        '0px 8px 16px rgba(0, 0, 0, 0.1)',
        '0px 16px 32px rgba(0, 0, 0, 0.1)',
        '0px 32px 64px rgba(0, 0, 0, 0.1)',
        '0px 2px 4px rgba(0, 0, 0, 0.05)',
        '0px 4px 8px rgba(0, 0, 0, 0.1)',
        '0px 8px 16px rgba(0, 0, 0, 0.1)',
        '0px 16px 32px rgba(0, 0, 0, 0.1)',
        '0px 32px 64px rgba(0, 0, 0, 0.1)',
        '0px 2px 4px rgba(0, 0, 0, 0.05)',
        '0px 4px 8px rgba(0, 0, 0, 0.1)',
        '0px 8px 16px rgba(0, 0, 0, 0.1)',
        '0px 16px 32px rgba(0, 0, 0, 0.1)',
        '0px 32px 64px rgba(0, 0, 0, 0.1)',
    ],
});

// Enhanced Styled Components
const StyledFormContainer = styled(Box)(({ theme, direction }) => ({
    minHeight: '100vh',
    padding: theme.spacing(1),
    direction: direction === 'rtl' ? 'rtl' : 'ltr',
    textAlign: direction === 'rtl' ? 'right' : 'left',
    [theme.breakpoints.down('md')]: {
        padding: theme.spacing(2),
    },
}));

const StyledCard = styled(Card)(({ theme }) => ({
    marginBottom: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[2],
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
        boxShadow: theme.shadows[4],
        transform: 'translateY(-2px)',
    },
}));

const StyledCardHeader = styled(Box)(({ theme }) => ({
    padding: theme.spacing(3),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
    padding: theme.spacing(3),
    '&:last-child': {
        paddingBottom: theme.spacing(3),
    },
}));

const StyledSection = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

const StyledSectionTitle = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
    fontWeight: 600,
}));

const StyledTableContainer = styled(TableContainer)({
    marginTop: '5px',
    width: '82%',
    boxShadow: 'none',
    borderRadius: 0,
});

const StyledTable = styled(Table)({
    borderCollapse: 'collapse',
});

const StyledTableHead = styled(TableHead)({
    backgroundColor: '#BFC8FF',
    '& .MuiTableCell-head': {
        backgroundColor: '#BFC8FF',
        color: '#304FFF',
        fontFamily: 'Montserrat',
        fontSize: '14px',
        fontWeight: 600,
        textAlign: 'center',
        textTransform: 'uppercase',
        borderBottom: '2px solid #d3d3d3',
        position: 'sticky',
        top: 0
    },
});

const StyledTableRow = styled(TableRow)({
    '&:hover': {
    },
});

const StyledTableCellHeader = styled(TableCell)({
    backgroundColor: '#BFC8FF',
    color: '#304FFF',
    fontFamily: 'Montserrat',
    fontSize: '14px',
    fontWeight: 600,
    textAlign: 'center',
    textTransform: 'uppercase',
    borderBottom: '2px solid #d3d3d3',
    position: 'sticky',
    top: 0
});

const StyledTableCell = styled(TableCell)({
    borderTop: '1px solid rgba(191, 191, 193, 0.56)',
    backgroundColor: 'rgba(234, 237, 255, 0.32)',
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    textAlign: 'center',
    // Matches the default row height or margin/padding for your style
});

const StyledButton = styled(Button)(({ theme, variant = 'contained' }) => ({
    borderRadius: theme.shape.borderRadius,
    textTransform: 'none',
    fontWeight: 600,
    padding: theme.spacing(1.5, 3),
    transition: 'all 0.2s ease-in-out',
    ...(variant === 'contained' && {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            transform: 'translateY(-1px)',
            boxShadow: theme.shadows[4],
        },
    }),
    ...(variant === 'outlined' && {
        borderColor: theme.palette.primary.main,
        color: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.04),
            borderColor: theme.palette.primary.dark,
        },
    }),
}));

const StyledTextField = styled(TextField)(({ theme, direction }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: theme.shape.borderRadius,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
            },
        },
        '&.Mui-focused': {
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
                borderWidth: 2,
            },
        },
    },
    direction: direction === 'rtl' ? 'rtl' : 'ltr',
}));

const InvoiceModalTypes = [
    {
        controller: 'Incomes',
        endPoint: 'GenerateIncomePdf',
        draftEndPoint: 'GenerateIncomeDraftPdf',
        type: '',
        TransactionDocumentType: '',
        TransactionDocumentTitle: '',
        title: 'generateInvoice',
        screen: '/Document',
        type: 'Incomes',
    },
    {
        controller: 'Incomes',
        endPoint: 'GenerateRefundIncomePdf',
        draftEndPoint: 'GenerateRefundIncomeDraftPdf',
        type: '',
        TransactionDocumentType: '',
        TransactionDocumentTitle: '',
        title: 'generateCreditNote',
        screen: '/Document',
        type: 'Incomes',
    },
    {
        controller: "TransactionDocuments",
        endPoint: "GenerateTransactionDocumentPdf",
        draftEndPoint: "GenerateTransactionDocumentDraftPdf",
        type: "quotation", TransactionDocumentType: "Quotation",
        TransactionDocumentTitle: "הצעת מחיר",
        title: "generateQuotation",
        screen: '/TransactionDocuments',
        type: 'TransactionDocuments',
    },
    {
        controller: "TransactionDocuments",
        endPoint: "GenerateTransactionDocumentPdf",
        draftEndPoint: "GenerateTransactionDocumentDraftPdf",
        type: "order",
        TransactionDocumentType: "Order",
        TransactionDocumentTitle: "הזמנה",
        title: "generateOrder",
        screen: '/TransactionDocuments',
        type: 'TransactionDocuments',
    },
    {
        controller: "TransactionDocuments",
        endPoint: "GenerateTransactionDocumentPdf",
        draftEndPoint: "GenerateTransactionDocumentDraftPdf",
        type: "transaction",
        TransactionDocumentType: "Transaction",
        TransactionDocumentTitle: "חשבון עסקה",
        title: "generateTransactionInvoice",
        screen: '/TransactionDocuments',
        type: 'TransactionDocuments',

    },
];

const CreateInvoice = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    useRequireDocumentTypeAccess(location.state?.type);
    const [accountCardOptions, setAccountCardOptions] = useState([]);
    const [sortCodeIdOptions, setSortCodeIdOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [creditAccountOptions, setCreditAccountOptions] = useState([]);
    const [debitAccountOptions, setDebitAccountOptions] = useState([]);
    const [addNewAccountCardDocument, setAddNewAccountCardDocument] = useState(false);
    const [showAllAccountCards, setShowAllAccountCards] = useState(false);
    const [accountCardEditDownloadOptions, setAccountCardEditDownloadOptions] = useState([]);
    const [direction, setDirection] = useState('rtl'); // Default to 'rtl'
    const [clientItems, setClientItems] = useState([]);
    const [inventoryStockByItemId, setInventoryStockByItemId] = useState({});
    const [isFirstUserFetched, setIsFirstUserFetched] = useState(false);
    const [editedItem, setEditedItem] = useState({});
    const [token, setToken] = useState('');
    const [data, setData] = useState([]);
    const [client, setClient] = useState(null);
    const [datePickerVisibility, setDatePickerVisibility] = useState(null);
    const [datePickerKey, setDatePickerKey] = useState(0);
    const [type, setType] = useState(null);
    const [user, setUser] = useState(null);
    const [cardIdOption, setCardIdOption] = useState(-1);
    const [newItems, setNewItems] = useState([]); // To store new items
    const [addItemFlags, setAddItemFlags] = useState([true]); // Array of booleans to track add new item mode per index
    const [pricePickerState, setPricePickerState] = useState(null);
    const [invoiceSignature, setInvoiceSignature] = useState('');
    const [invoiceReference, setInvoiceReference] = useState('');
    const [signatureReferenceFilled, setSignatureReferenceFilled] = useState(false);
    const [authUrl, setAuthUrl] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [resumeInvoiceCreation, setResumeInvoiceCreation] = useState(false);
    const [draftPdfModalVisible, setDraftPdfModalVisible] = useState(false);
    const [PdfModalVisible, setPdfModalVisible] = useState(false);
    const [draftPdfPath, setDraftPdfPath] = useState('');
    const [draftPdfBlob, setDraftPdfBlob] = useState(null);
    const [PdfBlob, setPdfBlob] = useState(null);
    const [isTaxAuthorityAuthModalVisible, setIsTaxAuthorityAuthModalVisible] = useState(false);
    const [isTaxAuthorityErrorModalVisible, setIsTaxAuthorityErrorModalVisible] = useState(false);
    const [pendingDecisionStatus, setPendingDecisionStatus] = useState(null);
    const [resolvePendingMode, setResolvePendingMode] = useState(false);
    const [invoiceModalType, setInvoiceModalType] = useState(0); // Default to 0 for TaxInvoice
    const sigPad = useRef(null);
    // When an invoice is generated from the Expense Assignment report, its line items are
    // created ad-hoc. This flag + list let us remove those auto-created client items once the
    // invoice is issued, so they don't pollute the client's item catalog. Other flows are
    // unaffected because the flag is only ever set from that report's navigation state.
    const deleteGeneratedClientItemsRef = useRef(false);
    const generatedClientItemsToDeleteRef = useRef([]);
    // Always-fresh copy of the loaded client items, so async prefills (which run inside a
    // setTimeout with a stale closure) can compute the next makat (last makat + 1) correctly.
    const clientItemsRef = useRef([]);
    // Expense ids this invoice is issued for (Expense Assignment report); marked as invoiced on success.
    const sourceExpenseIdsRef = useRef([]);
    const [signatureReferenceInvoiceModalVisible, setSignatureReferenceInvoiceModalVisible] = useState(false);
    const [authModalVisible, setAuthModalVisible] = useState(false);
    const [authWindow, setAuthWindow] = useState(null);
    const [needSignature, setNeedSignature] = useState(false);
    const [needReference, setNeedReference] = useState(false);
    const [totalsVisible, setTotalsVisible] = useState(true);
    const { setMenuProps } = useHamburgerMenu();
    const { viewDocumentDetails, viewTransactionDocuments, viewPaymentAcceptanceDetails } = useClientNavigation(client);
    const [useEnhancedVersion, setUseEnhancedVersion] = useState(true); // Toggle for enhanced version

    const [selectedLanguage, setSelectedLanguage] = useState(i18n.language || 'he'); // Default to current UI language
    const [itemsDisplayMode, setItemsDisplayMode] = useState('normal'); // 'normal' or 'descriptionOnly'

    const isInventoryDocument =
        invoiceModalType === Constants.TransactionDocumentsNames.taxInvoice ||
        invoiceModalType === Constants.TransactionDocumentsNames.creditInvoice ||
        invoiceModalType === Constants.TransactionDocumentsNames.taxInvoiceReceipt;

    useEffect(() => {
        setMenuProps({ client });
    }, [client, setMenuProps]);

    // Fetch document settings when client is available
    useEffect(() => {
        const fetchDocumentSettings = async () => {
            if (client?.caseId && token) {
                try {
                    const settings = await GetDocumentSettingsByCaseId(client.caseId, token);
                    const settingsObj = Array.isArray(settings) ? settings[0] : settings;
                    if (settingsObj?.itemsDisplayMode) {
                        setItemsDisplayMode(settingsObj.itemsDisplayMode);
                    }
                } catch (error) {
                    console.error('Error fetching document settings:', error);
                    // Default to 'normal' if fetch fails
                    setItemsDisplayMode('normal');
                }
            }
        };
        fetchDocumentSettings();
    }, [client?.caseId, token]);

    // Sync document language with UI language changes
    useEffect(() => {
        setSelectedLanguage(i18n.language || 'he');
        setFormData(prev => ({
            ...prev,
            language: i18n.language || 'he'
        }));
    }, [i18n.language]);

    const handleTaxAuthorityAuth = (authUrl) => {
        // Open the authentication URL in a new tab

        const authWindow = window.open(authUrl, '_blank');

        // Store the authWindow in state
        setAuthWindow(authWindow);

        // Start checking for authentication completion
        checkAuthCompletion(authWindow);
    };
    const [formData, setFormData] = useState({
        invoiceClientName: '',
        invoiceClientCaseId: '',
        invoiceAccountCodeId: '',
        invoiceClientEmail: '',
        invoiceClientPhoneNumber: '',
        invoiceClientLocation: '',
        invoiceDescription: '',
        sendToCreator: false,
        priceIncludingVAT: true,
        invoiceDate: (() => {
            // Get Israel time (Asia/Jerusalem), then convert to UTC
            const israelDate = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Jerusalem" }));
            return new Date(Date.UTC(
                israelDate.getFullYear(),
                israelDate.getMonth(),
                israelDate.getDate(),
                israelDate.getHours(),
                israelDate.getMinutes(),
                israelDate.getSeconds(),
                israelDate.getMilliseconds()
            ));
        })(),
        reportingMonthDate: (() => {
            const israelDate = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Jerusalem" }));
            return new Date(Date.UTC(
                israelDate.getFullYear(),
                israelDate.getMonth(),
                israelDate.getDate(),
                israelDate.getHours(),
                israelDate.getMinutes(),
                israelDate.getSeconds(),
                israelDate.getMilliseconds()
            ));
        })(),
        invoiceItems: [],
        forceNoTax: false,
        noNeedforConfirmationNumber: false,
        language: i18n.language || 'he', // Default to current UI language
    });
    const [VAT_RATE, setVatRate] = useState(getVatValue(formData.invoiceDate));

    useEffect(() => {
        if (formData?.invoiceDate) {
            setVatRate(getVatValue(formData.invoiceDate));
        }
    }, [formData]);



    const computeRowTotals = (item) => {
        const qty = parseFloat(item.quantity) || 0;
        const price = parseFloat(item.price) || 0;
        const discountPercent = parseFloat(item.discount) || 0;

        const base = qty * price;
        const discountAmount = (base * discountPercent) / 100;
        const totalAfterDiscount = base - discountAmount;

        return { discountAmount, totalAfterDiscount };
    };


    const computeTotalInvoiceAmount = () => {
        if (!client || !client.caseType) {
            return 0;
        }
        
        const isTaxExemptOrNoTax = Constants.TaxExemptTypes.includes(client.caseType) || formData.forceNoTax;
        
        if (formData.foreignCurrencyCode != null) {
            return formData.invoiceItems.reduce((sum, item) => {
                const price = parseFloat(item.price) || 0;
                const quantity = parseFloat(item.quantity) || 1;
                const discountPercent = parseFloat(item.discount) || 0;
                let baseAmount = price * quantity;
                let discountedPrice = baseAmount - (baseAmount * discountPercent) / 100;
                
                // Tax Exempt / forceNoTax: total is sum of item amounts only, no VAT
                if (isTaxExemptOrNoTax) {
                    return sum + discountedPrice;
                }
                
                // Check if this specific item should include VAT
                const itemIncludeVat = item.includeVat !== undefined ? item.includeVat : true;
                
                if (formData.priceIncludingVAT) {
                    return sum + discountedPrice;
                } else {
                    return sum + (itemIncludeVat ? discountedPrice * (1 + VAT_RATE) : discountedPrice);
                }
            }, 0);
        }
        
        return formData.invoiceItems.reduce((sum, item) => {
            let price = parseFloat(item.price) || 0;
            const quantity = parseFloat(item.quantity) || 1;
            const discountPercent = parseFloat(item.discount) || 0;
            let baseAmount = price * quantity;
            let discountedPrice = baseAmount - (baseAmount * discountPercent) / 100;
            
            if (isTaxExemptOrNoTax) {
                return sum + discountedPrice;
            } else {
                // Check if this specific item should include VAT
                const itemIncludeVat = item.includeVat !== undefined ? item.includeVat : true;
                
                if (formData.priceIncludingVAT) {
                    // Global setting is PriceIncludingVAT
                    if (itemIncludeVat) {
                        // Item also has VATCalculation - price already includes VAT
                        return sum + discountedPrice;
                    } else {
                        // Item has NoVATCalculation - price includes VAT but we don't want to calculate VAT for this item
                        // So we return the price as is (it's already the final amount)
                        return sum + discountedPrice;
                    }
                } else {
                    // Global setting is PriceExcludingVAT
                    if (itemIncludeVat) {
                        // Item has VATCalculation - add VAT to the price
                        return sum + (discountedPrice * (1 + VAT_RATE));
                    } else {
                        // Item has NoVATCalculation - price is without VAT and we don't add VAT
                        return sum + discountedPrice;
                    }
                }
            }
        }, 0);
    };

    const totalInvoiceAmount = computeTotalInvoiceAmount();

    // "שיוך מסמכים" (create mode): an invoice (TaxInvoice) links to the quotations/orders/transactions
    // /delivery-notes it bills; an Order or Transaction links to the earlier documents it is based on.
    // A quotation (root) and a credit note have no sources, so linking is not offered there.
    const invoiceLinkingDocumentType =
        invoiceModalType === 0 ? 4 /* TaxInvoice */
        : invoiceModalType === 3 ? 2 /* Order */
        : invoiceModalType === 4 ? 3 /* Transaction */
        : 0;
    const invoiceLinkingItems = useMemo(
        () => toLinkDraftItems(formData.invoiceItems),
        [formData.invoiceItems]
    );
    const invoiceLinking = useDocumentLinking({
        entityType: invoiceModalType === 0 ? ENTITY_TYPE.INCOME : ENTITY_TYPE.TRANSACTION_DOCUMENT,
        documentType: invoiceLinkingDocumentType,
        customerAccountCodeId: formData.invoiceAccountCodeId ? String(formData.invoiceAccountCodeId) : '',
        customerName: formData.invoiceClientName,
        amount: totalInvoiceAmount,
        currency: formData.foreignCurrencyCode === 0 || formData.foreignCurrencyCode
            ? formData.foreignCurrencyCode
            : CURRENCY_ILS,
        items: invoiceLinkingItems,
        enabled: invoiceModalType === 0 || invoiceModalType === 3 || invoiceModalType === 4,
    });

    const linkedDocumentDescription = () =>
        pendingLinksToLinkedDescription(
            invoiceLinking.pendingLinks,
            (type) => Constants.TransactionDocuments?.[type],
            t('Document')
        );

    // Function to calculate total amount without VAT for display
    const computeTotalAmountWithoutVAT = () => {
        if (!client || !client.caseType) {
            return 0;
        }

        const isTaxExemptOrNoTax = Constants.TaxExemptTypes.includes(client.caseType) || formData.forceNoTax;

        if (isTaxExemptOrNoTax) {
            return totalInvoiceAmount;
        }

        return formData.invoiceItems.reduce((sum, item) => {
            let price = parseFloat(item.price) || 0;
            const quantity = parseFloat(item.quantity) || 1;
            const discountPercent = parseFloat(item.discount) || 0;
            let baseAmount = price * quantity;
            let discountedPrice = baseAmount - (baseAmount * discountPercent) / 100;

            // Check if this specific item should include VAT
            const itemIncludeVat = item.includeVat !== undefined ? item.includeVat : true;

            if (!itemIncludeVat) {
                // Item has NoVATCalculation - include the full amount in "without VAT" total
                return sum + discountedPrice;
            }

            if (formData.priceIncludingVAT) {
                // Global setting is PriceIncludingVAT
                // Extract price without VAT
                const priceWithoutVAT = discountedPrice / (1 + VAT_RATE);
                return sum + priceWithoutVAT;
            } else {
                // Global setting is PriceExcludingVAT
                // Price is already without VAT
                return sum + discountedPrice;
            }
        }, 0);
    };

    const totalAmountWithoutVAT = computeTotalAmountWithoutVAT();

    // Check if complex VAT logic is needed (when items have different VAT settings)
    const needsComplexVatLogic = () => {
        // If forceNoTax is true, no complex VAT logic is needed
        if (formData.forceNoTax) {
            return false;
        }
        
        if (!formData.invoiceItems || formData.invoiceItems.length === 0) {
            return false;
        }
        
        // Check if all items have the same VAT setting
        const firstItemVatSetting = formData.invoiceItems[0]?.includeVat !== undefined 
            ? formData.invoiceItems[0].includeVat 
            : true;
            
        const allItemsHaveSameVatSetting = formData.invoiceItems.every(item => {
            const itemVatSetting = item.includeVat !== undefined ? item.includeVat : true;
            return itemVatSetting === firstItemVatSetting;
        });
        
        // If all items have the same VAT setting, no complex logic is needed
        if (allItemsHaveSameVatSetting) {
            return false;
        }
        
        // Check if any item has a different VAT setting than the global setting
        const globalVatSetting = formData.priceIncludingVAT;
        return formData.invoiceItems.some(item => {
            const itemVatSetting = item.includeVat !== undefined ? item.includeVat : true;
            return itemVatSetting !== globalVatSetting;
        });
    };
    
    // Check if all items have NoVATCalculation (includeVat = false)
    const allItemsHaveNoVATCalculation = () => {
        if (!formData.invoiceItems || formData.invoiceItems.length === 0) {
            return false;
        }
        
        return formData.invoiceItems.every(item => {
            const itemVatSetting = item.includeVat !== undefined ? item.includeVat : true;
            return itemVatSetting === false; // NoVATCalculation
        });
    };
    
    // The limit is evaluated on the amount before VAT, so it matches the threshold
    // the tax authority applies when requiring a confirmation number.
    const [isAmountAboveLimit, setIsAmountAboveLimit] = useState(totalAmountWithoutVAT >= getIsraelInvoiceAmountLimit(formData.invoiceDate));
    useEffect(() => {
        if (formData?.invoiceDate) {
            setIsAmountAboveLimit(totalAmountWithoutVAT >= getIsraelInvoiceAmountLimit(formData.invoiceDate));
        }
    }, [formData, totalAmountWithoutVAT]);


    const allClientItemFieldsFilled =
        formData.invoiceClientName &&
        (!(formData.invoiceClientEmail || formData.invoiceClientEmail === '') || validateEmail(formData.invoiceClientEmail)) && // Ensure email is valid only if it's entered
        formData.reportingMonthDate &&
        formData.invoiceDate &&
        formData.invoiceItems.length > 0 &&
        (!isAmountAboveLimit || isValidShaamCustomerVatNumber(formData.invoiceClientCaseId) || formData.noNeedforConfirmationNumber || invoiceModalType === 1 || invoiceModalType === 4) &&
        formData.invoiceItems.every((item) => {
            const hasRequiredFields = item.name && item.makat && item.price;
            // Discount stricter validation
            const discountInvalid = (
                item.discount === null ||
                item.discount === undefined ||
                item.discount === '' ||
                isNaN(item.discount) ||
                item.discount < 0 ||
                item.discount > 100
            );
            return hasRequiredFields && !discountInvalid;
        });

    const fieldErrors = {
        invoiceAccountCodeId: !formData.invoiceAccountCodeId,
        invoiceClientName: !formData.invoiceClientName,
        invoiceClientPhoneNumber:
            !!formData.invoiceClientPhoneNumber && !validatePhoneNumber(formData.invoiceClientPhoneNumber),
        invoiceClientEmail: formData.invoiceClientEmail && !validateEmail(formData.invoiceClientEmail),
        reportingMonthDate: !formData.reportingMonthDate,
        invoiceDate: !formData.invoiceDate,
        invoiceItemsEmpty: formData.invoiceItems.length === 0,
        invoiceClientCaseId: isAmountAboveLimit && !isValidShaamCustomerVatNumber(formData.invoiceClientCaseId) && !formData.noNeedforConfirmationNumber && invoiceModalType !== 1 && invoiceModalType !== 4,
        invoiceItems: formData.invoiceItems.map(item => ({
            name: !item.name,
            makat: !item.makat,
            price: !item.price,
            quantity: !item.quantity || item.quantity <= 0,
            discount: (
                item.discount === null ||
                item.discount === undefined ||
                item.discount === '' ||
                isNaN(item.discount) ||
                item.discount < 0 ||
                item.discount > 100
            )
        })),
    };


    const handleSignatureChange = () => {
        if (sigPad.current) {
            setInvoiceSignature(sigPad.current.getTrimmedCanvas().toDataURL('image/png'));
            setSignatureReferenceFilled(invoiceSignature && invoiceReference);
        }
    };

    const handleSignatureReferenceSave = async () => {
        if (needSignature && !invoiceSignature) {
            toast.error(t('ProvideSignature'));
            return;
        }
        if (needReference && !invoiceReference) {
            toast.error(t('ProvideSignature'));
            return;
        }

        // TaxInvoice and InvoiceReceipt (ReceiptTax) share the same invoice numbering
        // series, so their reference numbers must be at least 1000 apart. This is only
        // enforced against a sibling that has actually been customized (not its default).
        if (needReference && invoiceReference) {
            const refKey = getReferenceKeyAndDefault(invoiceModalType)?.key;
            const siblingInfo =
                refKey === 'taxInvoiceReference'
                    ? { key: 'recieptTaxReference', default: 1000, messageKey: 'ReceiptInvoiceReferenceMustBeApart' }
                    : refKey === 'recieptTaxReference'
                        ? { key: 'taxInvoiceReference', default: 0, messageKey: 'InvoiceReferenceMustBeApart' }
                        : null;
            if (siblingInfo) {
                const enteredValue = parseInt(invoiceReference, 10);
                try {
                    const referencesResponse = await DocumentService.GetReferenceByCaseId(client.caseId, token);
                    const siblingValue = parseInt(referencesResponse?.data?.[siblingInfo.key], 10);
                    const siblingCustomized = !isNaN(siblingValue) && siblingValue !== siblingInfo.default;
                    if (siblingCustomized && !isNaN(enteredValue) && Math.abs(enteredValue - siblingValue) < 1000) {
                        toast.error(t(siblingInfo.messageKey, { current: siblingValue, recommended: siblingValue + 1000 }));
                        return;
                    }
                } catch (error) {
                    console.error('Error validating reference gap:', error);
                }
            }
        }

        setSignatureReferenceInvoiceModalVisible(false);
        // Proceed with next steps
    };

    const handleSignatureReferenceClose = async () => {
        setInvoiceSignature('');
        setInvoiceReference('');
        setSignatureReferenceInvoiceModalVisible(false);
    };

    const clearSignature = () => {
        sigPad.current.clear();
        setInvoiceSignature('');
        setSignatureReferenceFilled(false);
    };

    useEffect(() => {
        const fetchUserData = async () => {
            setLoading(true);
            try {
                const currentUser = await UserService.getCurrentUser();
                const currentToken = await UserService.getCurrentToken();
                if (currentUser && currentToken) {
                    setUser(currentUser);
                    setToken(currentToken);

                    if (location.state && location.state.client && location.state.type) {
                        setClient(location.state.client);
                        setType(location.state.type);

                        // Refetch client data to get the latest information
                        try {
                            const updatedClient = await ClientService.GetClient(currentToken, location.state.client.caseId);
                            setClient(updatedClient);
                        } catch (error) {
                            console.error('Error refetching client data:', error);
                        }

                        // Map the location.state.type to the correct invoiceModalType index
                        const typeMapping = {
                            'TaxInvoice': Constants.TransactionDocumentsNames.taxInvoice,
                            'CreditNote': Constants.TransactionDocumentsNames.creditInvoice,
                            'Quotation': Constants.TransactionDocumentsNames.quotation,
                            'Order': Constants.TransactionDocumentsNames.order,
                            'Transaction': Constants.TransactionDocumentsNames.transaction,
                            'Receipt': Constants.TransactionDocumentsNames.receipt, // 6
                        };

                        const modalTypeIndex = typeMapping[location.state.type];

                        if (modalTypeIndex !== undefined) {
                            setInvoiceModalType(modalTypeIndex);
                        } else {
                            // Handle unknown type, maybe set a default or show an error
                            console.error('Unknown invoice type:', location.state.type);
                            // You can set a default or handle it as needed
                            setInvoiceModalType(0); // Default to 'taxInvoice' if unknown
                        }

                        await fetchEnums(currentUser, currentToken, location.state.client);
                        
                        // Handle duplicate data if present (after fetchEnums to have accountCardOptions)
                        if (location.state.item) {
                            const duplicateItem = location.state.item;

                            // Expense Assignment report: clean up auto-created client items post-issue.
                            deleteGeneratedClientItemsRef.current = !!duplicateItem.deleteGeneratedItemsAfterCreate;
                            generatedClientItemsToDeleteRef.current = [];
                            sourceExpenseIdsRef.current = Array.isArray(duplicateItem.sourceExpenseIds)
                                ? duplicateItem.sourceExpenseIds
                                : [];

                            // Wait a bit for accountCardOptions to be set
                            setTimeout(() => {
                                // Populate form data with duplicate document data
                                setFormData((prevFormData) => {
                                    // Find the matching account card to get email, phone, location
                                    const matchingAccountCard = accountCardEditDownloadOptions.find(
                                        option => option.cardId === duplicateItem.accountCodeId
                                    );
                                    
                         
                                    
                                    return {
                                        ...prevFormData,
                                        invoiceClientName: duplicateItem.accountCodeName || '',
                                        invoiceClientCaseId: duplicateItem.accountCaseId || '',
                                        invoiceAccountCodeId: duplicateItem.accountCodeId ? parseInt(duplicateItem.accountCodeId, 10) : '',
                                        invoiceClientEmail: matchingAccountCard?.email || '',
                                        invoiceClientPhoneNumber: matchingAccountCard?.phoneNumber || '',
                                        invoiceClientLocation: duplicateItem.location || matchingAccountCard?.location || '',
                                        invoiceDescription: duplicateItem.description || '',
                                        invoiceDate: duplicateItem.documentDate ? duplicateItem.documentDate : new Date(),
                                        reportingMonthDate: duplicateItem.reportingMonthDate || duplicateItem.documentDate ? new Date(duplicateItem.reportingMonthDate || duplicateItem.documentDate) : new Date(),
                                        priceIncludingVAT: duplicateItem.priceIncludingVAT !== undefined ? duplicateItem.priceIncludingVAT : true,
                                        forceNoTax: duplicateItem.vat === 0 ? true : (duplicateItem.forceNoTax || false),
                                        foreignCurrencyCode: duplicateItem.foreignCurrencyCode || null,
                                        invoiceItems: (() => {
                                            // Seed the makat counter from the largest existing makat
                                            // (catalog + incoming lines) so generated items get last+1.
                                            const existingMakats = [
                                                ...(clientItemsRef.current || []).map(ci => parseInt(ci.makat, 10) || 0),
                                                ...(duplicateItem.items || []).map(it => parseInt(it.makat, 10) || 0),
                                            ];
                                            let nextMakat = (existingMakats.length ? Math.max(...existingMakats) : 0) + 1;
                                            return duplicateItem.items?.map(item => ({
                                                // Auto-assign last-makat + 1 to lines that arrive without one.
                                                makat: item.makat || String(nextMakat++),
                                                name: item.name || '',
                                                quantity: item.quantity || 1,
                                                price: item.price || 0,
                                                discount: item.discount || 0,
                                                itemId: item.itemId || item.id,
                                                isNew: !item.makat, // If no makat, it's a new item that needs to be created
                                                includeVat: item.includeVat !== undefined ? item.includeVat : true,
                                            })) || [];
                                        })(),
                                    };
                                });
                                
                            }, 100);
                        }
                    }
                } else {
                    navigate('/login');
                }
            } catch (error) {
                // Handle error - maybe navigate to login or show a message
                console.error('Error fetching user data:', error);
            }
            setLoading(false);
        };

        fetchUserData();
        // Include dependencies in the dependency array
    }, [location.state.client, location.state.type]);

    useEffect(() => {
        // Update direction based on the current language
        if (i18n.language === 'he' || i18n.language === 'ar') {
            setDirection('rtl');
        } else {
            setDirection('ltr');
        }
    }, [i18n.language]);

    useEffect(() => {
        clientItemsRef.current = clientItems;
    }, [clientItems]);


    const fetchEnums = async (user, token, client) => {
        setLoading(true);
        try {
            const [codeIdResponse, accountsResponse, clientItemsResponse] = await Promise.all([
                EnumsService.GetSortCodesByAccountantId(client.accountantId, token),
                EnumsService.GetAccountCardsByCaseId(client.caseId, token),
                EnumsService.GetClientItemsByCaseId(client.caseId, token),
            ]);

            if (codeIdResponse.ok) {
                const codeIdData = await codeIdResponse.json();
                setSortCodeIdOptions(codeIdData);
            }

            if (accountsResponse.ok) {
                const accountsData = await accountsResponse.json();
                setCreditAccountOptions(accountsData);
                setDebitAccountOptions(accountsData);
                setAccountCardEditDownloadOptions(accountsData);
                const accountCardOptions = [{ name: t('addCustomer'), value1: 'AddItem', isEditable: true }, ...accountsData];

                setAccountCardOptions(accountCardOptions);
            }

            if (clientItemsResponse.ok) {
                const clientItemsData = await clientItemsResponse.json();
                // clientItemsData.unshift({ makat: 'add-item', name: t('AddItem') });

                setClientItems(clientItemsData);

                // Pre-fill invoice items table with default item if set and table is empty
                const defaultItem = Array.isArray(clientItemsData) ? clientItemsData.find(i => i.isDefaultItem === true) : null;
                if (defaultItem && (defaultItem.makat || defaultItem.Makat)) {
                    setFormData(prev => {
                        if (prev.invoiceItems && prev.invoiceItems.length > 0) return prev;
                        const row = {
                            makat: defaultItem.makat ?? defaultItem.Makat ?? '',
                            name: defaultItem.name ?? defaultItem.Name ?? '',
                            quantity: 1,
                            price: defaultItem.price ?? defaultItem.Price ?? '',
                            discount: 0,
                            isNew: false,
                            includeVat: defaultItem.includeVat ?? defaultItem.IncludeVat ?? true,
                            itemId: defaultItem.id ?? defaultItem.Id ?? null
                        };
                        return { ...prev, invoiceItems: [row] };
                    });
                    setAddItemFlags([true]);
                }
            }

            setIsFirstUserFetched(true);

            // Load current inventory stock levels (by ClientItem id) so we can warn on over-selling
            if (client?.managesInventory) {
                try {
                    const invResp = await InventoryService.GetInventoryItemsWithClientItemByCaseId(
                        client.caseId,
                        null,
                        token,
                    );
                    if (invResp.ok) {
                        const { success, data } = await invResp.json();
                        if (success && Array.isArray(data)) {
                            const map = {};
                            data.forEach((it) => {
                                if (it.clientItemId) {
                                    map[it.clientItemId] = Number(it.currentQuantity) || 0;
                                }
                            });
                            setInventoryStockByItemId(map);
                        }
                    }
                } catch (err) {
                    console.error('Error fetching inventory stock for invoice:', err);
                }
            }

            try {
                const projectsRes = await ProjectsService.getProjects(client.caseId, token);
                if (projectsRes.ok) {
                    const projectsData = await projectsRes.json();
                    setSegmentationProjects(Array.isArray(projectsData) ? projectsData : []);
                }
            } catch (err) {
                console.error('Error fetching segmentations for invoice:', err);
            }
        } catch (error) {
            console.error('Error fetching enum data:', error);
            toast.error(t('enumFetchError', { errorText: error.message }));
        } finally {
            setLoading(false);
        }
    };

    const refreshSegmentationProjects = useCallback(async () => {
        if (!client?.caseId || !token) return;
        try {
            const projectsRes = await ProjectsService.getProjects(client.caseId, token);
            if (projectsRes.ok) {
                const projectsData = await projectsRes.json();
                setSegmentationProjects(Array.isArray(projectsData) ? projectsData : []);
            }
        } catch (err) {
            console.error('Error refreshing segmentations for invoice:', err);
        }
    }, [client?.caseId, token]);

    const handleCloseAddNewAccountCardDocument = () => {
        setFormData((prev) => ({
            ...prev,
            invoiceAccountCodeId: '',
            invoiceClientCaseId: '',
            invoiceClientName: '',
            invoiceClientEmail: '',
            invoiceClientPhoneNumber: '',
            invoiceClientLocation: '',
        }));
        setAddNewAccountCardDocument(false);
    };

    // useEffect(() => {
    //     if (client) {
    //         if (client?.signature && client?.invoicesNumbers) {
    //             // setInvoiceModalVisible(true); // If you have an invoice modal
    //         } else {
    //             setSignatureReferenceInvoiceModalVisible(true);
    //         }
    //     }
    // }, [client]);

    const handleChange = (field, value) => {

        setFormData((prevState) => ({
            ...prevState,
            [field]: value,
        }));
    };

    const recomputeDiscountPercentFromAmount = (item) => {
        const qty = parseFloat(item.quantity) || 0;
        const price = parseFloat(item.price) || 0;
        const base = qty * price;
        const amt = parseFloat(item.discountAmount);
        if (!isFinite(amt) || base <= 0) {
            return 0;
        }
        const pct = (amt / base) * 100;
        return Math.round(pct * 10000) / 10000;
    };

    const handleItemChange = (index, field, value) => {
        setFormData((prevState) => {
            const updatedItems = [...prevState.invoiceItems];
            const item = { ...updatedItems[index], [field]: value };

            if (field === 'discount') {
                // User edited percent directly; drop the locally-typed amount.
                delete item.discountAmount;
            } else if (
                (field === 'price' || field === 'quantity') &&
                item.discountAmount !== undefined &&
                item.discountAmount !== null &&
                item.discountAmount !== ''
            ) {
                // Keep the user's fixed discount amount; recompute percent
                // against the new base (qty * price).
                item.discount = recomputeDiscountPercentFromAmount(item);
            }

            updatedItems[index] = item;
            return {
                ...prevState,
                invoiceItems: updatedItems,
            };
        });
    };

    const handleItemDiscountAmountChange = (index, amountStr) => {
        setFormData((prevState) => {
            const updatedItems = [...prevState.invoiceItems];
            const item = { ...updatedItems[index], discountAmount: amountStr };
            if (amountStr === '' || amountStr === null || amountStr === undefined) {
                item.discount = 0;
            } else {
                item.discount = recomputeDiscountPercentFromAmount(item);
            }
            updatedItems[index] = item;
            return { ...prevState, invoiceItems: updatedItems };
        });
    };

    const handleItemVATChange = (index, value) => {
        // If forceNoTax is true, don't allow VAT changes
        if (formData.forceNoTax) {
            return;
        }
        
        setFormData((prevState) => {
            const updatedItems = [...prevState.invoiceItems];
            const item = updatedItems[index];
            
            // Update the VAT setting
            updatedItems[index].includeVat = value;
            
            // If this is an existing item (not new), update it in the database
            if (!item.isNew && item.itemId) {
                updateClientItemVATSetting(item, value);
            }
            
            // If NoVATCalculation is selected (value is false), automatically set forceNoTax to true
            // and set all items to not include VAT
            // if (value === false) {
            //     const allItemsNoVAT = updatedItems.map(item => ({ ...item, includeVat: false }));
            //     return {
            //         ...prevState,
            //         invoiceItems: allItemsNoVAT,
            //         forceNoTax: true,
            //         priceIncludingVAT: false,
            //         noNeedforConfirmationNumber: false,
            //     };
            // }
            
            return {
                ...prevState,
                invoiceItems: updatedItems,
            };
        });
    };

    const updateClientItemVATSetting = async (item, newVatSetting) => {
        try {
            console.log(item);
            
            const response = await ClientItemsService.UpdateClientItem(
                client.caseId,
                item.makat, // original makat
                item.makat, // new makat (same)
                item.name,
                item.price,
                newVatSetting, // new VAT setting
                "",
                token
            );
            
            if (!response.ok) {
                const errorText = await response.text();
                toast.error(t('FailedToUpdateClientItem') + `: ${errorText}`);
            } else {
                // Optionally show success message
                // toast.success(t('ClientItemUpdatedSuccessfully'));
            }
        } catch (error) {
            console.error('Error updating client item VAT setting:', error);
            toast.error(t('ErrorUpdatingClientItem'));
        }
    };

    const handleAddItem = () => {
        setAddItemFlags((prevFlags) => [...prevFlags, true]);
        const newItem = { 
            makat: '', 
            name: '', 
            notes: '',
            quantity: 1, 
            price: 0, 
            discount: 0, 
            isNew: true,
        includeVat: formData.forceNoTax ? false : true // Set default VAT setting based on forceNoTax or global setting
        };
        let newMakat = 1; // Default makat for the first item
        const allMakats = [
            ...clientItems.map(item => parseInt(item.makat, 10) || 0),
            ...formData.invoiceItems.map(item => parseInt(item.makat, 10) || 0)
        ];

        if (allMakats.length > 0) {
            // Find the largest makat among them
            const maxMakat = Math.max(...allMakats);
            newMakat = maxMakat + 1; // Increment the largest makat by 1
        }

        newItem.makat = newMakat.toString();
        setFormData((prevState) => ({
            ...prevState,
            invoiceItems: [...prevState.invoiceItems, newItem],
        }));
    };

    const handleRemoveItem = (index) => {
        setFormData((prevState) => {
            const updatedItems = prevState.invoiceItems.filter((item, idx) => idx !== index);
            return {
                ...prevState,
                invoiceItems: updatedItems,
            };
        });
        setAddItemFlags((prevFlags) => prevFlags.filter((flag, idx) => idx !== index));
    };


    const handleItemChangeMany = (index, updates) => {
        const discountChanged = 'discount' in updates;
        const baseChanged = 'price' in updates || 'quantity' in updates;
        setFormData(prevFormData => ({
            ...prevFormData,
            invoiceItems: prevFormData.invoiceItems.map((item, idx) => {
                if (idx !== index) return item;
                const merged = { ...item, ...updates };
                if (discountChanged) {
                    // Percent set explicitly; abandon any typed fixed amount.
                    delete merged.discountAmount;
                } else if (
                    baseChanged &&
                    merged.discountAmount !== undefined &&
                    merged.discountAmount !== null &&
                    merged.discountAmount !== ''
                ) {
                    merged.discount = recomputeDiscountPercentFromAmount(merged);
                }
                return merged;
            })
        }));
    };

    const handleInvoiceItem = async (index, selectedOption) => {
        if (!selectedOption) {
            // If cleared (newValue === null)
            handleItemChangeMany(index, {
                makat: '',
                name: '',
                notes: '',
                price: '',
                quantity: '',
                discount: '',
                itemId: null,
                isNew: false,
                foreignCurrencyPrice: '',
                includeVat: formData.forceNoTax ? false : true, // Preserve VAT setting based on forceNoTax
            });

            const updatedFlags = [...addItemFlags];
            updatedFlags[index] = false;
            setAddItemFlags(updatedFlags);
            return;
        }
        if (selectedOption.value === 'add-item') {
            const updatedFlags = [...addItemFlags];
            updatedFlags[index] = true;
            let newMakat = 1; // Default makat for the first item

            const allMakats = [
                ...clientItems.map(item => parseInt(item.makat, 10) || 0),
                ...formData.invoiceItems.map(item => parseInt(item.makat, 10) || 0)
            ];

            if (allMakats.length > 0) {
                // Find the largest makat among them
                const maxMakat = Math.max(...allMakats);
                newMakat = maxMakat + 1; // Increment the largest makat by 1
            }

            // Update the item fields
            // handleItemChange(index, 'makat', newMakat.toString());
            // handleItemChange(index, 'name', '');
            handleItemChangeMany(index, {
                makat: newMakat.toString(),
                name: '',
                itemId: null,
                isNew: true,   // Now you can store the "new" state in the item itself
                foreignCurrencyPrice: '',
                includeVat: formData.forceNoTax ? false : true // Set default VAT setting based on forceNoTax
            });
            setAddItemFlags(updatedFlags);
        } else {
            const item = clientItems.find((option) => option.makat === selectedOption.value);
            // handleItemChange(index, 'makat', item.makat);
            // handleItemChange(index, 'name', item.name);
            // handleItemChange(index, 'price', item.price);

            // handleItemChange(index, 'isNew', false);
            let updates = {
                makat: item.makat,
                name: item.name,
                itemId: item.id,
                isNew: false,
                includeVat: formData.forceNoTax ? false : (item.includeVat !== undefined ? item.includeVat : true), // Use forceNoTax setting or item's VAT setting if available, otherwise use global setting
                quantity: itemsDisplayMode === 'descriptionOnly' ? 1 : (item.quantity || 1) // Force quantity to 1 in descriptionOnly mode
            };

            const applyChosenPrice = async (chosenAmount) => {
                if (formData.foreignCurrencyCode != null && formData.invoiceDate) {
                    let rate = currencyRateValue;
                    if (!rate) {
                        rate = await ForeignCurrencyService.GetIlsRateForDate(
                            formData.foreignCurrencyCode,
                            formData.invoiceDate
                        );
                        setCurrencyRate(rate);
                    }
                    updates.foreignCurrencyPrice = chosenAmount;
                    updates.price = Number((chosenAmount * rate).toFixed(2));
                } else {
                    updates.price = chosenAmount;
                }
                handleItemChangeMany(index, updates);
                const updatedFlags = [...addItemFlags];
                updatedFlags[index] = false;
                setAddItemFlags(updatedFlags);
            };

            const selection = resolveItemPriceSelection(item);
            if (selection.needsPicker) {
                setPricePickerState({
                    index,
                    itemName: item.name,
                    prices: selection.prices,
                    apply: applyChosenPrice,
                });
                return;
            }
            await applyChosenPrice(selection.amount);
        }
    };

    const handleAccountCodeChange = (selectedOption) => {
        if (selectedOption?.value1 === 'AddItem') {
            // Clear formData fields when adding a new item
            setFormData((prev) => ({
                ...prev,
                invoiceAccountCodeId: cardIdOption,
                invoiceClientCaseId: '',
                invoiceClientName: '',
                invoiceClientEmail: '',
                invoiceClientPhoneNumber: '',
                invoiceClientLocation: '',
            }));
            setAddNewAccountCardDocument(true);
        } else {
            // Set formData fields based on selected option
            const accountCard = accountCardOptions.find((option) => option.cardId?.toString() === selectedOption?.value?.toString());
            setFormData((prev) => ({
                ...prev,
                invoiceAccountCodeId: selectedOption?.value || '',
                invoiceClientCaseId: selectedOption?.accountCaseId || '',
                // Use selectedOption.name as primary source, fallback to accountCard.name
                invoiceClientName: selectedOption?.name || accountCard?.name || '',
                invoiceClientEmail: accountCard ? accountCard.email : '',
                invoiceClientLocation: accountCard ? accountCard.location : '',
                invoiceClientPhoneNumber: accountCard ? accountCard.phoneNumber : '',

            }));
        }
    };

    const formattedAccountCardOptions = accountCardOptions
        .filter((option) =>
            shouldIncludeClientAccountCard(option, {
                showAll: showAllAccountCards,
                selectedCardId: formData.invoiceAccountCodeId,
            })
        )
        .map(option => ({
            value: parseInt(option.cardId, 10), // Ensure it's a number
            label: option.accountCaseId
                ? `${option.name} - ${option.accountCaseId}`
                : `${option.name}`,
            name: option.name,
            accountCaseId: option.accountCaseId || '',
            cardId: parseInt(option.cardId, 10), // Ensure it's a number
            value1: option.value1,
            email: option.email || '',
        }));

    useEffect(() => {
        if (accountCardOptions && accountCardOptions.length > 0) {
            const maxCardId = Math.max(...accountCardOptions.slice(1).map((option) => parseInt(option.cardId, 10)));
            setCardIdOption(maxCardId + 1);
        }
    }, [accountCardOptions]);

    const handleCloseAddItem = (index) => {
        const updatedFlags = [...addItemFlags];
        updatedFlags[index] = false;
        setAddItemFlags(updatedFlags);
    };

    const handleAddMakatAndCheck = (index, value) => {
        // Check if the makat already exists in clientItems
        const existingItem = clientItems.find(item => item.makat === value);

        if (existingItem) {
            // Makat exists, alert the user or handle accordingly
            toast.error(t('MakatAlreadyExists'));
        } else {
            // Update the makat value
            handleItemChange(index, 'makat', value);
        }
    };

    // When user types/blurs makat: if it exists in clientItems, auto-fill item details (easier than select)
    const handleMakatBlurOrEnter = async (index, makatValue) => {
        const trimmed = String(makatValue || '').trim();
        if (!trimmed) return;

        const existingItem = clientItems.find(
            (item) => String(item.makat) === trimmed || item.makat === trimmed
        );
        if (!existingItem) return;

        const newValue = { value: existingItem.makat, label: existingItem.name };
        await handleInvoiceItem(index, newValue);
    };

    const validateInvoiceData = () => {
        let valid = true;

        if (formData.invoiceClientEmail && !validateEmail(formData.invoiceClientEmail)) {
            toast.error(t('InvalidEmailAddress'));
            valid = false;
        }

        if (formData.invoiceClientPhoneNumber && !validateNumber(formData.invoiceClientPhoneNumber)) {
            toast.error(t('InvalidPhoneNumber'));
            valid = false;
        }

        if (formData.invoiceClientCaseId && !validateNumber(formData.invoiceClientCaseId)) {
            toast.error(t('InvalidCaseId'));
            valid = false;
        }

        formData.invoiceItems.forEach((item) => {
            item.quantity = item.quantity?.toString().replace(/\s+/g, '') ?? '';
            item.price = item.price?.toString().replace(/\s+/g, '') ?? '';
            item.discount = item.discount?.toString().replace(/\s+/g, '') ?? '';

            if (item.quantity <= 0) {
                toast.error(t('invalidQuantity'));
                valid = false;
                return;
            }

            if (isNaN(item.price) || !validateAmount(item.price.toString())) {
                valid = false;
                toast.error(t('invalidPrice'));
                return;
            }

            if (isNaN(item.discount) || item.discount < 0 || item.discount > 100) {
                valid = false;
                toast.error(t('invalidDiscount'));
                return;
            }
        });

        return valid;
    };

    // const validateEmail = (email) => {
    //     // Simple email validation
    //     const re = /\S+@\S+\.\S+/;
    //     return re.test(email);
    // };

    const validateNumber = (number) => {
        const re = /^[0-9\b]+$/;
        return re.test(number);
    };

    const validateAmount = (amount) => {
        const re = /^-?\d+(\.\d+)?$/;
        return re.test(amount);
    };

    const [updateInventory, setUpdateInventory] = useState(false);
    const [segmentationProjects, setSegmentationProjects] = useState([]);
    const [selectedSegmentation, setSelectedSegmentation] = useState(null);


    /**
     * Compares the invoice line quantities (for items linked to inventory) against
     * the current stock. If any item would be reduced to zero or below, shows a
     * confirmation dialog. Returns true if the user wants to continue.
     */
    const confirmInventoryStockWarnings = async () => {
        const items = formData.invoiceItems || [];
        const warnings = [];
        items.forEach((item) => {
            const itemId = item.itemId;
            if (!itemId || item.isNew) return;
            if (!(itemId in inventoryStockByItemId)) return;
            const available = inventoryStockByItemId[itemId];
            const requested = Number(item.quantity) || 0;
            if (requested >= available) {
                warnings.push({ name: item.name || item.makat || itemId, available, requested });
            }
        });

        if (warnings.length === 0) return true;

        const lines = warnings
            .map((w) => `• ${w.name}: ${t('Available') || 'Available'} ${w.available}, ${t('Requested') || 'Requested'} ${w.requested}`)
            .join('<br/>');
        const res = await Swal.fire({
            title: t('StockDepletionWarningTitle') || 'Stock will reach zero',
            html: `${t('StockDepletionWarningBody') || 'The following items will reach 0 (stock will not go negative):'}<br/><br/>${lines}<br/><br/>${t('ContinueQuestion') || 'Do you want to continue?'}`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('Yes') || 'Yes',
            cancelButtonText: t('No') || 'No',
        });
        return res.isConfirmed;
    };

    const getDocumentTypeForPending = () => {
        // Invoice modal types: 0 TaxInvoice, 1 ReceiptTax, 4 CreditNote, etc.
        if (invoiceModalType === 1) return 'ReceiptTax';
        if (invoiceModalType === 4) return 'CreditNote';
        return 'TaxInvoice';
    };

    /**
     * Before draft/create: if next reference is locked by a failed TA confirmation,
     * open the decision modal and resolve using the first locked payload.
     * Returns true if the caller should proceed; false if blocked for user decision.
     */
    const ensureNoPendingDecision = async () => {
        // Only tax invoice / receipt tax flows need confirmation-number decision guard
        if (invoiceModalType !== 0 && invoiceModalType !== 1 && invoiceModalType !== 4) {
            return true;
        }
        try {
            const status = await TaxAuthorityService.getPendingInvoiceDecisionStatus(
                client.caseId,
                token,
                getDocumentTypeForPending()
            );
            // AwaitingDocument needs no decision but still blocks the reference: its document must
            // be issued on that same number before any new invoice can be created.
            const awaitingDocument =
                (status?.status || status?.Status) === 'AwaitingDocument';

            if (status?.requiresDecision || status?.RequiresDecision || awaitingDocument) {
                setPendingDecisionStatus(status);
                setResolvePendingMode(true);
                setIsTaxAuthorityErrorModalVisible(true);
                return false;
            }
            return true;
        } catch (e) {
            console.warn('Pending decision check failed; continuing', e);
            return true;
        }
    };

    const handleCreateDraftInvoice = async () => {
        if (client?.isActive === false) {
            toast.error(t('ClientInactiveCannotCreateDocuments'));
            return;
        }
        if (isInventoryDocument && client?.managesInventory) {
            const result = await Swal.fire({
                title: t('UpdateInventoryQuestion') || 'Do you want to update the inventory?',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: t('Yes') || 'Yes',
                cancelButtonText: t('No') || 'No',
            });

            if (result.isConfirmed) {
                // Warn if any item would deplete stock to zero or below
                const proceed = await confirmInventoryStockWarnings();
                if (!proceed) {
                    return;
                }
                // ✅ Add flag to formData to be used in handleCreateInvoice
                setUpdateInventory(true); // true means update inventory
            } else if (result.dismiss === Swal.DismissReason.cancel) {
                setUpdateInventory(false); // false means don't update inventory
            }
            else {
                // user closed the dialog – abort draft creation
                return;
            }
        }

        const canProceed = await ensureNoPendingDecision();
        if (!canProceed) {
            return;
        }

        setLoading(true);

        // Validate data
        let valid = validateInvoiceData();

        if (!valid) {
            setLoading(false);
            return;
        }

        // Determine VAT logic based on requirements
        let useComplexVatLogic = needsComplexVatLogic();
        let forceNoTax = formData.forceNoTax;
        let priceIncludingVAT = formData.priceIncludingVAT;
        
        // Case 1: When PriceIncludingVAT = true and all items have NoVATCalculation, set useComplexVatLogic = false
        if ( allItemsHaveNoVATCalculation()) {
            useComplexVatLogic = false;
        }
        
        // Case 2: When InvoiceWithNoTax is selected, set useComplexVatLogic = false and priceIncludingVAT = false
        if (formData.forceNoTax) {
            useComplexVatLogic = false;
        }
        
        // Case 3: When PriceIncludingVAT = true and all items have NoVATCalculation, enable ForceNoTax = true
        if ( allItemsHaveNoVATCalculation()) {
            forceNoTax = true;
        }
        
        const invoice = {
            CaseId: client.caseId,
            AccountantId: client.accountantId,
            Name: client.name,
            Description: linkedDocumentDescription() || formData.invoiceDescription,
            Reference: invoiceReference,
            ClientName: formData.invoiceClientName,
            ClientCaseId: formData.invoiceClientCaseId,
            PhoneNumber: client.phoneNumber,
            SecondaryPhoneNumber: client.secondaryPhoneNumber,
            Email: client.email,
            Website: client.website,
            Location: client.location,
            ClientLocation: formData.invoiceClientLocation,
            ClientEmail: formData.invoiceClientEmail,
            ClientPhoneNumber: formData.invoiceClientPhoneNumber,
            Date: formData.invoiceDate,
            Signature: invoiceSignature,
            AccountCodeId: formData.invoiceAccountCodeId.toString(),
            Items: formData.invoiceItems,
            PriceIncludingVAT: priceIncludingVAT,
            ForceNoTax: forceNoTax,
            ForeignCurrencyCode: formData.foreignCurrencyCode,
            useComplexVatLogic: useComplexVatLogic,
            Language: formData.language,
            SendToCreator: !!formData.sendToCreator,
        };

        try {
            // Call the API to generate draft invoice
            const pdfBlob = await DocumentService.CreateDraftInvoice(
                InvoiceModalTypes[invoiceModalType].controller,
                InvoiceModalTypes[invoiceModalType].draftEndPoint,
                invoice,
                InvoiceModalTypes[invoiceModalType].type,
                InvoiceModalTypes[invoiceModalType].TransactionDocumentTitle,
                InvoiceModalTypes[invoiceModalType].TransactionDocumentType,
                token
            );

            setDraftPdfBlob(pdfBlob);
            setDraftPdfModalVisible(true);

            toast.success(t('DraftDocumentCreated'));
        } catch (error) {
            console.error(error);
            toast.error(t('DraftDocumentDidNotCreated'));
        }

        setLoading(false);
    }

    const updateLocationState = async (newClientData, screen, type) => {

        const updatedClientData = {
            ...location.state.client,
            ...newClientData
        };

        // Determine which navigation function to call based on the screen
        if (InvoiceModalTypes[invoiceModalType].screen === '/Document') {
            viewDocumentDetails(InvoiceModalTypes[invoiceModalType].type);
        } else if (InvoiceModalTypes[invoiceModalType].screen === '/TransactionDocuments') {
            viewTransactionDocuments(InvoiceModalTypes[invoiceModalType].type);
        } else {
            // fallback to navigate if needed
            navigate(InvoiceModalTypes[invoiceModalType].screen, {
                replace: true,
                state: { client: updatedClientData, type: InvoiceModalTypes[invoiceModalType].type },
            });
        }
    };
    const handleSaveSignature = async (screen, type) => {
        const Client = await ClientService.GetClient(token, client.caseId);
        updateLocationState(Client, screen, type);

    }

    const saveNewItemsToDatabase = async (invoiceItems) => {
        try {

            // Filter out new items that need to be saved
            const itemsToSave = invoiceItems.filter(item => item.isNew);

            // Map items to the format expected by the API
            const itemsData = itemsToSave.map(item => ({
                makat: item.makat,
                name: item.name,
                caseId: client.caseId,
                price: item.price,
                includeVat: formData.forceNoTax ? false : (item.includeVat !== undefined ? item.includeVat : true) // Include VAT setting based on forceNoTax
                // Include other necessary fields if needed
            }));

            // Call the API to save new items
            const response = await ClientItemsService.AddClientItems(itemsData, token);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            const createdItems = data.items || [];
            // Update clientItems state with the new items
            setClientItems(prevClientItems => [...prevClientItems, ...itemsToSave]);

            // Clear newItems array after saving
            setFormData(prevState => ({
                ...prevState,
                invoiceItems: prevState.invoiceItems.map(item => ({ ...item, isNew: false })),
            }));
            return createdItems;
            // Optionally, update the addItemFlags if needed
        } catch (error) {
            console.error('Error saving new items:', error);
            toast.error(t('ErrorSavingNewItems'));
        }
    };

    const handleCreateInvoice = async (isAuthCompleted = false, decisionFlag = null, forceNoConfirmationNumber = false, resumeReference = null) => {
        if (!isAuthCompleted && decisionFlag === null) {
            const canProceed = await ensureNoPendingDecision();
            if (!canProceed) {
                return;
            }
        }

        setLoading(true);
        const VAT_RATE = getVatValue(formData.invoiceDate);


        // Validate data
        let valid = validateInvoiceData();

        if (!valid) {
            setLoading(false);
            return;
        }

        // "שיוך מסמכים": the linked amount may not exceed the document amount.
        if ((invoiceModalType === 0 || invoiceModalType === 3 || invoiceModalType === 4)
            && invoiceLinking.validation.overAllocated) {
            toast.error(t('LinkDocumentsOverAllocated'));
            setLoading(false);
            return;
        }

        // Ensure all invoiceItems have correct itemId before API call (like CreateReceipt.jsx)
        let updatedInvoiceItems = [...formData.invoiceItems];
        const itemsNeedingId = updatedInvoiceItems.filter(item => !item.isNew && (!item.itemId || item.itemId === 0));
        if (itemsNeedingId.length > 0) {
            try {
                const clientItemsResponse = await ClientItemsService.GetClientItemsByCaseId(client.caseId, token);
                if (clientItemsResponse.ok) {
                    const clientItemsData = await clientItemsResponse.json();
                    updatedInvoiceItems = updatedInvoiceItems.map(item => {
                        if (!item.isNew && (!item.itemId || item.itemId === 0)) {
                            const found = clientItemsData.find(ci => ci.makat === item.makat);
                            if (found && found.id) {
                                return { ...item, itemId: found.id };
                            }
                        }
                        return item;
                    });
                    setClientItems(clientItemsData); // Optionally update cache
                }
            } catch (err) {
                console.error('Failed to fetch latest client items for itemId update before API call', err);
            }
        }

        let newlyCreatedItems = [];
        if (updatedInvoiceItems.some(item => item.isNew)) {
            newlyCreatedItems = await saveNewItemsToDatabase(updatedInvoiceItems);
            // Track auto-created items for post-issue cleanup (Expense Assignment report flow).
            // Held in a ref so it survives the tax-authority auth "resume" round-trip.
            if (deleteGeneratedClientItemsRef.current && Array.isArray(newlyCreatedItems)) {
                generatedClientItemsToDeleteRef.current = [
                    ...generatedClientItemsToDeleteRef.current,
                    ...newlyCreatedItems.filter((ci) => ci?.makat != null && ci.makat !== ''),
                ];
            }
        }

        updatedInvoiceItems = updatedInvoiceItems.map((localItem) => {
            if (!localItem.isNew) {
                return localItem; // already had ID, so do nothing
            }
            // Find the matching newly created item by 'makat' or some unique field
            const matched = newlyCreatedItems.find(ci => ci.makat === localItem.makat);
            if (matched) {
                return {
                    ...localItem,
                    itemId: matched.id, // real DB ID
                    isNew: false,
                };
            }
            // If not found for some reason, just return the original
            return localItem;
        });

        // 4. Update your formData state so the items have real IDs
        setFormData(prev => ({
            ...prev,
            invoiceItems: updatedInvoiceItems
        }));
        const isTaxExemptOrNoTax = Constants.TaxExemptTypes.includes(client.caseType) || formData.forceNoTax;

        let totalInvoiceAmountWithoutVAT = 0;
        if (!isTaxExemptOrNoTax) {
            const VAT_RATE = getVatValue(formData.invoiceDate);
            totalInvoiceAmountWithoutVAT = formData.invoiceItems.reduce((sum, item) => {
                let price = parseFloat(item.price) || 0;
                price *= item.quantity || 1;
                const discountPercent = parseFloat(item.discount) || 0;
                const discountedPrice = price - (price * discountPercent) / 100;
                // If priceIncludingVAT, we back out the VAT
                if (formData.priceIncludingVAT) {
                    return sum + discountedPrice / (1 + VAT_RATE);
                } else {
                    return sum + discountedPrice;
                }
            }, 0);
        }
        // const totalInvoiceAmount = computeTotalInvoiceAmount();

        // Determine VAT logic based on requirements
        let useComplexVatLogic = needsComplexVatLogic();
        let forceNoTax = formData.forceNoTax;
        let priceIncludingVAT = formData.priceIncludingVAT;
        
        // Case 1: When PriceIncludingVAT = true and all items have NoVATCalculation, set useComplexVatLogic = false
        if ( allItemsHaveNoVATCalculation()) {
            useComplexVatLogic = false;
        }
        
        // Case 2: When InvoiceWithNoTax is selected, set useComplexVatLogic = false and priceIncludingVAT = false
        if (formData.forceNoTax) {
            useComplexVatLogic = false;
        }
        
        // Case 3: When PriceIncludingVAT = true and all items have NoVATCalculation, enable ForceNoTax = true
        if ( allItemsHaveNoVATCalculation()) {
            forceNoTax = true;
        }
        
        const invoice = {
            CaseId: client.caseId,
            AccountantId: client.accountantId,
            Name: client.name,
            Description: linkedDocumentDescription() || formData.invoiceDescription,
            // Resuming a decision sends the held reference so it is used as-is.
            Reference: resumeReference ?? invoiceReference,
            ClientName: formData.invoiceClientName,
            ClientCaseId: formData.invoiceClientCaseId,
            PhoneNumber: client.phoneNumber,
            SecondaryPhoneNumber: client.secondaryPhoneNumber,
            Email: client.email,
            Website: client.website,
            Location: client.location,
            ClientLocation: formData.invoiceClientLocation,
            ClientEmail: formData.invoiceClientEmail,
            ClientPhoneNumber: formData.invoiceClientPhoneNumber,
            Date: formData.invoiceDate,
            reportingMonthDate: formData.reportingMonthDate,
            Signature: invoiceSignature,
            AccountCodeId: formData.invoiceAccountCodeId.toString(),
            Items: updatedInvoiceItems,
            PriceIncludingVAT: priceIncludingVAT,
            ForceNoTax: forceNoTax,
            addToInventory: updateInventory,
            foreignCurrencyCode: formData.foreignCurrencyCode,
            noNeedforConfirmationNumber: formData.noNeedforConfirmationNumber || forceNoConfirmationNumber,
            Decision: decisionFlag, // Option 4 (Request for Use): create without confirmation number
            useComplexVatLogic: useComplexVatLogic,
            Language: formData.language,
            SegmentationId: selectedSegmentation?.id ?? null,
            SendToCreator: !!formData.sendToCreator,
            // Client Expense Assignment "issue tax invoice" flow: the expense ids this invoice is
            // generated from. Persisted on the Income so the report can mark those rows as invoiced.
            SourceExpenseIds: sourceExpenseIdsRef.current?.length ? sourceExpenseIdsRef.current : null,
        };

        // "שיוך מסמכים": documents the user linked while creating this document. The generate
        // endpoint persists them atomically as GeneratedFrom / PaymentFor relationships.
        if (invoiceModalType === 0 || invoiceModalType === 3 || invoiceModalType === 4) {
            const linkPayload = invoiceLinking.payload;
            if (linkPayload.sourceDocumentIds.length > 0) {
                // Keep any sources already set by the automatic generation flow (e.g. coming from an
                // order) and add the ones chosen in the dialog, without duplicating.
                const existing = Array.isArray(invoice.SourceDocumentIds) ? invoice.SourceDocumentIds : [];
                const merged = Array.from(new Set([...existing, ...linkPayload.sourceDocumentIds]));
                invoice.SourceDocumentIds = merged;
                if (linkPayload.sourceAllocations.length > 0) {
                    invoice.SourceAllocations = [
                        ...(Array.isArray(invoice.SourceAllocations) ? invoice.SourceAllocations : []),
                        ...linkPayload.sourceAllocations,
                    ];
                }
            }
        }

        const isNeedToAskAboutConfirmationNumber =
            (invoiceModalType === 0 )
            && !isTaxExemptOrNoTax && !formData.noNeedforConfirmationNumber;
        if (
            !isTaxExemptOrNoTax &&
            totalInvoiceAmountWithoutVAT >= getIsraelInvoiceAmountLimit(formData.invoiceDate) &&
            !isAuthCompleted &&
            isNeedToAskAboutConfirmationNumber
        ) {
            try {
                

                const tokenData = await TaxAuthorityService.requestTokenClient(client.caseId, token, Constants.TaxAuthorityScopes.CreationNumber);

                if (tokenData?.authUrl) {
                    handleTaxAuthorityAuth(tokenData.authUrl);
                    setLoading(false);
                    return; // Wait for auth to complete
                }
            } catch (error) {
                console.error('Error fetching token:', error);
                toast.error(t('tokenFetchError'));
                setLoading(false);
                return;
            }
        }

        // Proceed to create invoice
        try {
            setDraftPdfBlob(null);
            setDraftPdfModalVisible(false);
            
            const response = await DocumentService.CreateNewInvoice(
                InvoiceModalTypes[invoiceModalType].controller,
                InvoiceModalTypes[invoiceModalType].endPoint,
                invoice,
                InvoiceModalTypes[invoiceModalType].type,
                InvoiceModalTypes[invoiceModalType].TransactionDocumentTitle,
                InvoiceModalTypes[invoiceModalType].TransactionDocumentType,
                token
            );
            
            // Check if the response is a success message (string) or PDF blob
            if (typeof response === 'string') {
                // This is a success message from the backend (e.g., "Decision processed successfully")
                if (response === 'Decision processed successfully') {
                    await Swal.fire({
                        title: t('Success') || 'Success!',
                        text: t('TaxAuthorityRequestSent'),
                        icon: 'success',
                        timer: 2000,
                        showConfirmButton: false
                    });
                } else {
                    toast.success(response);
                }
                setLoading(false);
                handleInvoiceClose();
                return;
            }

            // Check if response is a text blob (success message) or PDF blob
            if (response instanceof Blob && response.type === 'text/plain') {
                const textResponse = await response.text();
                if (textResponse === 'Decision processed successfully') {
                    await Swal.fire({
                        title: t('Success') || 'Success!',
                        text: t('TaxAuthorityRequestSent'),
                        icon: 'success',
                        timer: 2000,
                        confirmButtonText: t('OK') || 'OK!'
                    });
                } else {
                    toast.success(textResponse);
                }
                setLoading(false);
                handleInvoiceClose();
                return;
            }

            // This is the expected PDF blob
            const pdfBlob = response;


            if (addNewAccountCardDocument) {
                await EnumsService.AddAccountCard(
                    client.caseId,
                    formData.invoiceAccountCodeId.toString(),
                    formData.invoiceClientName,
                    formData.invoiceClientCaseId,
                    "לקוחות",
                    token,
                    formData.invoiceClientPhoneNumber,
                    formData.invoiceClientLocation,
                    formData.invoiceClientEmail
                );
            }
            else {
                await EnumsService.UpdateAccountCardCaseId(client.caseId, formData.invoiceAccountCodeId.toString(), formData.invoiceClientCaseId, formData.invoiceClientEmail, formData.invoiceClientPhoneNumber, formData.invoiceClientLocation, token);
            }
            setPdfBlob(pdfBlob);
            setPdfModalVisible(true);

            toast.success(t('InvoiceCreated'));

            // The originating expense rows are marked as invoiced server-side (the Income carries
            // SourceExpenseIds); just clear the local ref now that the invoice was created.
            if (sourceExpenseIdsRef.current.length) {
                sourceExpenseIdsRef.current = [];
            }

            // Expense Assignment report flow: the invoice's line items were auto-created only to
            // issue this invoice, so delete those client items now to keep the catalog clean.
            if (deleteGeneratedClientItemsRef.current && generatedClientItemsToDeleteRef.current.length) {
                const itemsToDelete = generatedClientItemsToDeleteRef.current;
                generatedClientItemsToDeleteRef.current = [];
                deleteGeneratedClientItemsRef.current = false;
                try {
                    await Promise.all(
                        itemsToDelete.map((ci) =>
                            ClientItemsService.DeleteClientItem(ci.makat, client.caseId, token)
                        )
                    );
                    setClientItems((prev) =>
                        prev.filter(
                            (ci) => !itemsToDelete.some((del) => String(del.makat) === String(ci.makat))
                        )
                    );
                } catch (cleanupError) {
                    console.error('Error cleaning up generated client items:', cleanupError);
                }
            }

            handleInvoiceClose();
        } catch (error) {
            console.error(error);
            const errorMessage = error.message || "";
            
            // Check for error codes in different formats
            // Always show the toast first
            let errorCodeMatch = errorMessage.match(/Error Code (\d+):/); // Extracts the number after "Error Code"
            if (!errorCodeMatch) {
                errorCodeMatch = errorMessage.match(/Status Code: (\d+),/); // Extracts the number after "Status Code:"
            }
            if (errorCodeMatch) {
                const errorCode = errorCodeMatch[1]; // Capture the error code
                toast.error(t(`TaxInvoiceError${errorCode}`), { autoClose: false }); // Use the dynamic translation key and keep the toast open
                
                // Check if we should show the tax authority error modal for specific error codes
                if (errorCode === Constants.TaxAuthorityErrorCodes.INVOICE_NOT_APPROVED.toString() ||
                errorCode === Constants.TaxAuthorityErrorCodes.NO_DECISION_SENT.toString() ||
                errorCode === Constants.TaxAuthorityErrorCodes.REFERENCE_AWAITING_DOCUMENT.toString()) {
                    try {
                        const status = await TaxAuthorityService.getPendingInvoiceDecisionStatus(
                            client.caseId,
                            token,
                            getDocumentTypeForPending()
                        );
                        setPendingDecisionStatus(status);
                        setResolvePendingMode(
                            !!(status?.requiresDecision || status?.RequiresDecision) ||
                            (status?.status || status?.Status) === 'AwaitingDocument'
                        );
                    } catch (e) {
                        setResolvePendingMode(false);
                        setPendingDecisionStatus(null);
                    }
                    setIsTaxAuthorityErrorModalVisible(true);
                }
            } else {
                toast.error(errorMessage); // Fallback message if no error code found
            }
        }

        setLoading(false);
    };

    const handleTaxAuthorityOptionSelect = (decisionFlag, options) => {
        // Option 4 (Request for Use): create invoice without confirmation number via noNeedforConfirmationNumber flag
        // if (options?.forceNoConfirmationNumber) {
        //     handleCreateInvoice(true, null, true);
        //     return;
        // }
        // Allow 0 and any number as valid decisionFlag
        if (decisionFlag !== undefined && decisionFlag !== null) {
            handleCreateInvoice(true, decisionFlag);
        }
    };

    /**
     * Issue a held document from the invoice the Tax Authority decision was accepted for.
     *
     * The decision covers the invoice SHAAM is holding under that number, so its own lines are the
     * ones that have to be issued. The form usually shows a different invoice by now — the one the
     * user was typing when the held number blocked them — and posting that would put those lines on
     * the held number while the real invoice stays unissued.
     */
    const finishHeldInvoiceFromSnapshot = async (status, decisionFlag) => {
        setLoading(true);
        try {
            const generated = await TaxAuthorityService.generateDocumentWithDecision(
                status,
                decisionFlag,
                token
            );

            if (generated?.type === 'text') {
                toast.success(generated.message || t('TaxAuthorityRequestSent'));
                handleInvoiceClose();
                return;
            }

            let blob = generated?.type === 'pdf' ? generated.blob : null;
            if (generated?.type === 'json') {
                const { presignedUrl, pdfBytes } = generated.data || {};
                if (pdfBytes) {
                    const bytes = Uint8Array.from(atob(pdfBytes), (c) => c.charCodeAt(0));
                    blob = new Blob([bytes], { type: 'application/pdf' });
                } else if (presignedUrl) {
                    blob = await (await fetch(presignedUrl)).blob();
                }
            }

            if (blob) {
                setPdfBlob(blob);
                setPdfModalVisible(true);
            }

            toast.success(t('InvoiceCreated'));
            handleInvoiceClose();
        } catch (error) {
            console.error(error);
            const message = error?.message || '';
            toast.error(
                message === 'MissingStoredInvoiceSnapshot'
                    ? t('MissingStoredInvoiceSnapshot')
                    : message || t('ErrorGeneratingInvoice'),
                { autoClose: false }
            );
        } finally {
            setLoading(false);
        }
    };

    const handlePendingDecisionResolved = ({ decisionFlag, action, invoiceId }) => {
        const status = {
            ...(pendingDecisionStatus || {}),
            invoiceId: invoiceId || pendingDecisionStatus?.invoiceId || pendingDecisionStatus?.InvoiceId,
            InvoiceId: invoiceId || pendingDecisionStatus?.invoiceId || pendingDecisionStatus?.InvoiceId,
        };
        setResolvePendingMode(false);
        setPendingDecisionStatus(null);

        // Cancel still issues a document: a zero-amount canceled placeholder with Decision=0.
        const isCancel = decisionFlag === 0 || action === 'Cancel';
        if (isCancel) {
            toast.info(t('InvoiceCancelledSuccessfully'));
        }

        const flag = isCancel ? 0 : decisionFlag;

        // The decision covers the invoice SHAAM is holding, so issue that snapshot — never the
        // form the user happens to be filling (often a different invoice that tripped the 4900).
        finishHeldInvoiceFromSnapshot(status, flag);
    };


    const handleInvoiceClose = async () => {
        // Refetch client data to get updated information
        try {
            const updatedClient = await ClientService.GetClient(token, client.caseId);
            setClient(updatedClient);
        } catch (error) {
            console.error('Error refetching client data:', error);
        }

        setFormData({
            invoiceClientName: '',
            invoiceClientCaseId: '',
            invoiceAccountCodeId: '',
            invoiceClientEmail: '',
            invoiceClientPhoneNumber: '',
            invoiceClientLocation: '',
            invoiceDescription: '',
            sendToCreator: false,
            priceIncludingVAT: true,
            invoiceDate: new Date(),
            reportingMonthDate: new Date(),
            invoiceItems: [],
            addToInventory: updateInventory,
            forceNoTax: false,
            noNeedforConfirmationNumber: false,
            language: i18n.language || 'he'
        });
        setAddItemFlags([]);
        setInvoiceSignature('');
        setInvoiceReference('');
        setAddNewAccountCardDocument(false);
        setSelectedSegmentation(null);
        // setNeedReference(false);
        // setNeedSignature(false);
    };
    const handleReset = () => {
        // ... existing reset logic
        setNeedSignature(false);
        setNeedReference(false);
    };

    // Helper function to get reference key and default value for specific modal type
    const getReferenceKeyAndDefault = (modalType) => {
        switch (modalType) {
            case 0: // taxInvoice
                return { key: 'taxInvoiceReference', default: 0 };
            case 1: // creditNote
                return { key: 'creditNoteReference', default: 2000 };
            case 2: // quotation
                return { key: 'quotationReference', default: 0 };
            case 3: // order
                return { key: 'orderReference', default: 0 };
            case 4: // transaction
                return { key: 'transactionReference', default: 0 };
            case 5: // invoiceReceipt
                return { key: 'recieptTaxReference', default: 1000 };
            case 6: // receipt
                return { key: 'receiptReference', default: 0 };
            default:
                return null;
        }
    };

    // Helper function to check if reference for specific modal type is at default
    const checkIfReferenceIsDefault = (references, modalType) => {
        const refInfo = getReferenceKeyAndDefault(modalType);
        if (!refInfo) return false;
        
        return references[refInfo.key] === refInfo.default;
    };

    useEffect(() => {
        const checkReferencesAndSignature = async () => {
            if (!client || !token) return;

            let hasSignature = client.signature;
            let hasDocNumber = false;
            let referencesAreDefault = false;

            // Fetch references and check if they're at default values for this specific modal type
            try {
                const referencesResponse = await DocumentService.GetReferenceByCaseId(client.caseId, token);
                if (referencesResponse.success && referencesResponse.data) {
                    referencesAreDefault = checkIfReferenceIsDefault(referencesResponse.data, invoiceModalType);
                }
            } catch (error) {
                console.error('Error fetching references:', error);
                referencesAreDefault = true; // Assume default if fetch fails
            }
            switch (invoiceModalType) {
                case 0: // taxInvoice
                case 5: // invoiceReceipt
                case 1: //refund
                    hasDocNumber = (client.invoicesNumbers && client.invoicesNumbers !== 0) || (client.refundInvoicesNumbers && client.refundInvoicesNumbers !== 0);
                    break;
                case 2: // quotation
                    hasDocNumber = client.quotationInvoicesNumbers && client.quotationInvoicesNumbers !== 0;
                    break;
                case 3: // order
                    hasDocNumber = client.orderInvoicesNumbers && client.orderInvoicesNumbers !== 0;
                    break;
                case 4: // transactionInvoice
                    hasDocNumber = client.transactionInvoicesNumbers && client.transactionInvoicesNumbers !== 0;
                    break;
                case 6: // receipt
                    hasDocNumber = client.receiptNumbers && client.receiptNumbers !== 0;
                    break;
                default:
                    hasDocNumber = false;
                    break;
            }

            // If references are at default values, need to collect reference
            if (referencesAreDefault) {
                if (hasSignature) {
                    // Has signature but references are default, need to collect reference only
                    setNeedSignature(false);
                    setNeedReference(true);
                    setSignatureReferenceInvoiceModalVisible(true);
                } else {
                    // No signature and references are default, need to collect both signature and reference
                    setNeedSignature(true);
                    setNeedReference(true);
                    setSignatureReferenceInvoiceModalVisible(true);
                }
                return;
            }

            // References are NOT at default (they've been set/customized)
            // Only check for signature, never ask for reference again
            if (hasSignature) {
                // Has signature and references are set, no need for modal
                setNeedSignature(false);
                setNeedReference(false);
                setSignatureReferenceInvoiceModalVisible(false);
            } else {
                // No signature but references are set, need to collect signature only
                setNeedSignature(true);
                setNeedReference(false);
                setSignatureReferenceInvoiceModalVisible(true);
            }
        };

        checkReferencesAndSignature();
    }, [client, invoiceModalType, token]);

    const handleVatRadioChange = (e) => {
        setEditedItem({ ...editedItem, ["vat"]: parseFloat(e.target.value) });
    };

    // const handleTaxAuthorityAuth = (authUrl) => {
    //     const width = 600;
    //     const height = 600;
    //     const left = window.screen.width / 2 - width / 2;
    //     const top = window.screen.height / 2 - height / 2;
    //     const authWindow = window.open(authUrl, '_blank', `width=${width},height=${height},left=${left},top=${top}`);

    //     const receiveMessage = (event) => {
    //         if (event.origin !== window.location.origin) {
    //             return;
    //         }
    //         if (event.data === 'authComplete') {
    //             window.removeEventListener('message', receiveMessage);
    //             if (authWindow && !authWindow.closed) {
    //                 authWindow.close();
    //             }
    //             checkAuthCompletion();
    //         }
    //     };

    //     window.addEventListener('message', receiveMessage, false);
    // };

    const checkAuthCompletion = async (authWindow) => {
        setLoading(true);
        try {
            let attempts = 0;
            const maxAttempts = 25;
            while (attempts < maxAttempts) {
                // Check if the authWindow was closed by the user
                if (authWindow.closed) {
                    setLoading(false);
                    toast.error(t('Authentication window was closed by the user.'));
                    return;
                }

                const result = await TaxAuthorityService.checkAuthCompletionClient(client.caseId, Constants.TaxAuthorityScopes.CreationNumber);
                if (result.completed) {
                    setLoading(false);
                    setResumeInvoiceCreation(true);
                    // Close the auth window
                    if (authWindow && !authWindow.closed) {
                        authWindow.close();
                    }
                    return;
                } else {
                    attempts += 1;
                    await new Promise((resolve) => setTimeout(resolve, 3000));
                }
            }
            setLoading(false);
            toast.error(t('tokenFetchError'));
            // Close the auth window on timeout
            if (authWindow && !authWindow.closed) {
                authWindow.close();
            }
        } catch (error) {
            console.error('Error checking auth completion:', error);
            setLoading(false);
            toast.error(t('tokenFetchError'));
            // Close the auth window on error
            if (authWindow && !authWindow.closed) {
                authWindow.close();
            }
        }
    };

    useEffect(() => {
        if (resumeInvoiceCreation) {

            handleCreateInvoice(true); // Proceed with invoice creation
        }
    }, [resumeInvoiceCreation]);

    const handleCloseDraftPdfModal = () => {
        setDraftPdfBlob(null);
        setDraftPdfModalVisible(false);
    };

    const handleClosePdfModal = async () => {
        setPdfBlob(null);
        setPdfModalVisible(false);
        handleInvoiceClose();
        handleReset();

        // Stay on page: refresh client data without navigating away
        try {
            const updatedClient = await ClientService.GetClient(token, client.caseId);
            setClient(updatedClient);
            
            // Preserve the original type for navigation to ensure proper remapping on refresh
            // The type in location.state is used to map back to invoiceModalType via typeMapping
            // Map invoiceModalType index back to the capitalized type string that matches typeMapping keys
            const getTypeFromModalType = (modalType) => {
                switch (modalType) {
                    case Constants.TransactionDocumentsNames.taxInvoice:
                        return 'TaxInvoice';
                    case Constants.TransactionDocumentsNames.creditInvoice:
                        return 'CreditNote';
                    case Constants.TransactionDocumentsNames.quotation:
                        return 'Quotation';
                    case Constants.TransactionDocumentsNames.order:
                        return 'Order';
                    case Constants.TransactionDocumentsNames.transaction:
                        return 'Transaction';
                    case Constants.TransactionDocumentsNames.receipt:
                        return 'Receipt';
                    default:
                        return type || location.state?.type || InvoiceModalTypes[invoiceModalType]?.type;
                }
            };
            
            const navigationType = type || location.state?.type || getTypeFromModalType(invoiceModalType);
            
            navigate(location.pathname, {
                replace: true,
                state: { ...location.state, client: updatedClient, type: navigationType },
            });
        } catch (e) {
            console.error('Error refreshing client:', e);
        }
    };


    const validateDateAndShowWarning = async (selectedDate) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
        
        const selectedDateOnly = new Date(selectedDate);
        selectedDateOnly.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
        
        const isPastDate = selectedDateOnly < today;
        const isFutureDate = selectedDateOnly > today;
        
        if (isPastDate || isFutureDate) {
            const dateType = isPastDate ? 'past' : 'future';
            const result = await Swal.fire({
                title: t('DateWarning') || 'Date Warning',
                text: isPastDate 
                    ? (t('DocumentDateInPast') || 'The document date is in the past. Do you want to continue?')
                    : (t('DocumentDateInFuture') || 'The document date is in the future. Do you want to continue?'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: t('Yes') || 'Yes',
                cancelButtonText: t('No') || 'No',
                confirmButtonColor: '#304FFF',
                cancelButtonColor: '#d33'
            });
            
            return result.isConfirmed;
        }
        
        return true; // Date is today, no warning needed
    };

    const onDateChange = async (selectedDate) => {
        if (selectedDate) {
            // First validate the date and show warning if needed
            const shouldContinue = await validateDateAndShowWarning(selectedDate);
            if (!shouldContinue) {
                // User clicked "No", revert to current date (UTC)
                const today = new Date();
                const utcToday = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
                handleChange("invoiceDate", utcToday);
                handleChange("reportingMonthDate", utcToday);
                setDatePickerKey(prev => prev + 1); // Force re-render
                setDatePickerVisibility(false);
                return;
            }

            const newDate = new Date(Date.UTC(selectedDate.getFullYear(), selectedDate.getMonth(), 10));
            const response1 = await ClientService.CheckIfMonthClosed(client.caseId, newDate, token);
            if (response1.ok) {
                const isClosedReportingMonth = await response1.json();
                if (isClosedReportingMonth) {
                    toast.info(t('MonthReportAlreadyClosedTryAgain'))
                    // Revert to current date when month is closed
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    handleChange("invoiceDate", today);
                    handleChange("reportingMonthDate", today);
                    setDatePickerVisibility(false);
                    return
                }
                // Reset the time portion to avoid time zone offset issues
                selectedDate.setHours(0, 0, 0, 0);

                // Format the date manually or use toLocaleDateString for local date representation
                const year = selectedDate.getFullYear();
                const month = selectedDate.getMonth() + 1; // getMonth() is zero-based
                const day = selectedDate.getDate();

                // Ensure two digits for month and day
                const formattedDate = `${year}-${month?.toString().padStart(2, '0')}-${day?.toString().padStart(2, '0')}`;
                handleChange("invoiceDate", new Date(formattedDate));
                handleChange("reportingMonthDate", new Date(formattedDate));
            }
        }
    };

    const [currencyRateValue, setCurrencyRate] = useState(null);

    // Update handleCurrencyCodeChange to NOT overwrite the user's input, only recalc local currency
    const handleCurrencyCodeChange = async (newCode) => {
        handleChange('foreignCurrencyCode', newCode);
        if (newCode != null && formData.invoiceDate) {
            const rate = await ForeignCurrencyService.GetIlsRateForDate(
                newCode,
                formData.invoiceDate
            );
            setCurrencyRate(rate);
            setFormData((prevState) => {
                const updatedItems = prevState.invoiceItems.map((item) => {
                    // If the user already entered a price, treat it as the foreign currency price
                    let foreignCurrencyPrice = item.foreignCurrencyPrice;
                    if (foreignCurrencyPrice == null && item.price && !isNaN(item.price)) {
                        // If foreignCurrencyPrice is not set, but price is, treat price as foreign currency
                        foreignCurrencyPrice = parseFloat(item.price);
                    }
                    if (foreignCurrencyPrice != null && !isNaN(foreignCurrencyPrice)) {
                        const ilsPrice = Number((foreignCurrencyPrice * rate).toFixed(2));
                        return {
                            ...item,
                            foreignCurrencyPrice: foreignCurrencyPrice,
                            price: ilsPrice,
                        };
                    }
                    return item;
                });
                return {
                    ...prevState,
                    invoiceItems: updatedItems,
                };
            });
        }
    };

    // Update handleChangeItemPrice to always keep user's input as foreignCurrencyPrice
    const handleChangeItemPrice = async (index, value) => {
        handleItemChange(index, 'foreignCurrencyPrice', value);
        if (!value || isNaN(value)) {
            handleItemChange(index, 'price', 0);
            return;
        }
        if (formData.foreignCurrencyCode != null && formData.invoiceDate) {
            let rate = currencyRateValue;
            if (!rate) {
                rate = await ForeignCurrencyService.GetIlsRateForDate(
                    formData.foreignCurrencyCode,
                    formData.invoiceDate
                );
                setCurrencyRate(rate);
            }
            const amount = Number((value * rate).toFixed(2));
            handleItemChange(index, 'price', amount);
        } else {
            handleItemChange(index, 'price', value);
        }
    }

    const handlePriceIncludingVATChange = async (event) => {
        // If forceNoTax is true, don't allow priceIncludingVAT changes
        if (formData.forceNoTax) {
            return;
        }
        
        const newValue = event.target.value === 'true';
        setFormData((prevState) => ({
            ...prevState,
            priceIncludingVAT: newValue,
        }));

        // Only recalc if foreign currency is selected
        // if (formData.foreignCurrencyCode != null && formData.invoiceDate) {
        //     let rate = currencyRateValue;
        //     if (!rate) {
        //         rate = await ForeignCurrencyService.GetIlsRateForDate(
        //             formData.foreignCurrencyCode,
        //             formData.invoiceDate
        //         );
        //         setCurrencyRate(rate);
        //     }
        //     const VAT = getVatValue(formData.invoiceDate);
        //     setFormData((prevState) => {
        //         const updatedItems = prevState.invoiceItems.map((item) => {
        //             let foreignCurrencyPrice = item.foreignCurrencyPrice;
        //             if (foreignCurrencyPrice == null && item.price && !isNaN(item.price)) {
        //                 // If foreignCurrencyPrice is not set, but price is, treat price as foreign currency
        //                 foreignCurrencyPrice = parseFloat(item.price);
        //             }
        //             if (foreignCurrencyPrice != null && !isNaN(foreignCurrencyPrice)) {
        //                 let ilsPrice = Number((foreignCurrencyPrice * rate).toFixed(2));
        //                 if (!newValue) {
        //                     // Include VAT
        //                     ilsPrice = Number((ilsPrice * (1 + VAT)).toFixed(2));
        //                 } // else, price is just ilsPrice (without VAT)
        //                 return {
        //                     ...item,
        //                     price: ilsPrice,
        //                 };
        //             }
        //             return item;
        //         });
        //         return {
        //             ...prevState,
        //             invoiceItems: updatedItems,
        //         };
        //     });
        // }
    };


    const formattedCurrencyOptions = Object.entries(Constants.ForeignCurrencyCode).map(
        ([code, num]) => ({
            value: num,
            label: `${code} ${Constants.ForeignCurrencySymbol[code]}`,
        })
    );

    const handleGoBack = () => {
        updateLocationState(client, InvoiceModalTypes[invoiceModalType].screen, InvoiceModalTypes[invoiceModalType].type)
    }

    // Function to calculate total VAT amount for display
    const computeTotalVATAmount = () => {
        if (!client || !client.caseType) {
            return 0;
        }
        
        const isTaxExemptOrNoTax = Constants.TaxExemptTypes.includes(client.caseType) || formData.forceNoTax;
        
        if (isTaxExemptOrNoTax) {
            return 0;
        }
        
        return formData.invoiceItems.reduce((sum, item) => {
            let price = parseFloat(item.price) || 0;
            const quantity = parseFloat(item.quantity) || 1;
            const discountPercent = parseFloat(item.discount) || 0;
            let baseAmount = price * quantity;
            let discountedPrice = baseAmount - (baseAmount * discountPercent) / 100;
            
            // Check if this specific item should include VAT
            const itemIncludeVat = item.includeVat !== undefined ? item.includeVat : true;
            
            if (!itemIncludeVat) {
                // Item has NoVATCalculation - no VAT for this item
                return sum;
            }
            
            if (formData.priceIncludingVAT) {
                // Global setting is PriceIncludingVAT
                // Extract VAT from the price (price includes VAT)
                const priceWithoutVAT = discountedPrice / (1 + VAT_RATE);
                const itemVAT = discountedPrice - priceWithoutVAT;
                return sum + itemVAT;
            } else {
                // Global setting is PriceExcludingVAT
                // Calculate VAT on top of the price
                const itemVAT = discountedPrice * VAT_RATE;
                return sum + itemVAT;
            }
        }, 0);
    };
    
    const totalVATAmount = computeTotalVATAmount();

    return (
        <>
            <>
                {/* Form Inputs Section */}
                <style>
                    {`
                    .form-container2 {
                        animation: fadeIn 0.6s ease-out;
                        border-radius: 16px;
                        padding: 0.1rem;
                        margin: 0.1rem 1rem 0rem 1rem;
                 
                    }
                    
                    .form-section {
                        background: white;
                        border-radius: 16px;
                        padding: 1rem;
                        margin: 0.5rem 0;
                        box-shadow: 0 8px 32px rgba(0,0,0,0.1);
                        border: 1px solid rgba(255,255,255,0.2);
                        backdrop-filter: blur(10px);
                        animation: slideIn 0.8s ease-out;
                    }
                    
                    .form-row {
                        display: grid;
                        grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                        gap: 1rem;
                        margin: 1rem 0;
                    }
                    
                    .form-row-item {
                        position: relative;
                    }
                    
                    .form-row-item-title {
                        font-weight: 600;
                        color: #2c3e50;
                        margin-bottom: 0.5rem;
                        font-size: 0.95rem;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                    }
                    
                    .required-asterisk {
                        color: #e74c3c;
                        margin-left: 4px;
                    }
                    
                    .custom-input {
                        transition: all 0.3s ease;
                        border-radius: 12px;
                        border: 2px solid transparent;
                    }
                    
                    .custom-input:focus-within {
                        border-color: #304FFF;
                        box-shadow: 0 0 0 3px rgba(48, 79, 255, 0.1);
                        transform: translateY(-2px);
                    }
                    
                    .table-container {
                        background: white;
                        border-radius: 16px;
                        overflow: hidden;
                        box-shadow: 0 8px 32px rgba(0,0,0,0.1);
                        margin: 2rem 0;
                    }
                    
                    .table-header {
                        background: linear-gradient(135deg, #304FFF 0%, #1E40FF 100%);
                        color: white;
                        padding: 1rem;
                        font-weight: 600;
                    }
                    
                    
                    
                    .button-group {
                        display: flex;
                        gap: 1rem;
                        flex-wrap: wrap;
                        margin: 1rem 0;
                    }
                    
                    .action-button {
                        background: linear-gradient(135deg, #304FFF 0%, #1E40FF 100%);
                        color: white;
                        border: none;
                        padding: 0.75rem 1.5rem;
                        border-radius: 12px;
                        font-weight: 600;
                        cursor: pointer;
                        transition: all 0.3s ease;
                        display: flex;
                        align-items: center;
                        gap: 0.5rem;
                        box-shadow: 0 4px 15px rgba(48, 79, 255, 0.3);
                    }
                    
                    .action-button:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 6px 20px rgba(48, 79, 255, 0.4);
                    }
                    
                    .action-button:disabled {
                        background: linear-gradient(135deg, #6c757d 0%, #495057 100%);
                        cursor: not-allowed;
                        transform: none;
                        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                    }
                    
                    .section-title {
                        color: #2c3e50;
                        font-size: 1.8rem;
                        font-weight: 700;
                        margin: 0.5rem 0 0.5rem 0;
                        border-bottom: 3px solid #304FFF;
                        display: inline-block;
                        position: relative;
                    }
                    
                    .section-title::after {
                        content: '';
                        position: absolute;
                        bottom: -3px;
                        left: 0;
                        width: 50px;
                        height: 3px;
                        background: #e74c3c;
                    }
                    
                    .divider {
                        border-top: 2px solid #e9ecef;
                        margin: 2rem 0;
                        position: relative;
                    }
                    
                    .divider::before {
                        content: '';
                        position: absolute;
                        top: -1px;
                        left: 50%;
                        transform: translateX(-50%);
                        width: 100px;
                        height: 2px;
                        background: linear-gradient(90deg, transparent, #304FFF, transparent);
                    }
                    
                    @media (max-width: 768px) {
                        .form-row {
                            grid-template-columns: 1fr;
                        }

                        
                        .button-group {
                            flex-direction: column;
                        }
                        
                        .action-button {
                            width: 100%;
                            justify-content: center;
                        }
                    }
                `}
                </style>

                <InactiveClientAlert client={client} />

                <div className="form-container2">
                    <div className="form-section">


                        <EnhancedCreateInvoice
                            linking={invoiceLinking}
                            formData={formData}
                            setFormData={setFormData}
                            handleChange={handleChange}
                            handleAccountCodeChange={handleAccountCodeChange}
                            handleCurrencyCodeChange={handleCurrencyCodeChange}
                            handlePriceIncludingVATChange={handlePriceIncludingVATChange}
                            handleItemVATChange={handleItemVATChange}
                            handleInvoiceItem={handleInvoiceItem}
                            handleMakatBlurOrEnter={handleMakatBlurOrEnter}
                            handleItemChange={handleItemChange}
                            handleItemDiscountAmountChange={handleItemDiscountAmountChange}
                            handleChangeItemPrice={handleChangeItemPrice}
                            handleRemoveItem={handleRemoveItem}
                            handleAddItem={handleAddItem}
                            handleCreateDraftInvoice={handleCreateDraftInvoice}
                            handleCloseAddNewAccountCardDocument={handleCloseAddNewAccountCardDocument}
                            handleCloseAddItem={handleCloseAddItem}
                            onDateChange={onDateChange}
                            datePickerKey={datePickerKey}
                            computeRowTotals={computeRowTotals}
                            beautifyNumber={beautifyNumber}
                            styledInput={styledInput}
                            Constants={Constants}
                            direction={direction}
                            t={t}
                            currentUILanguage={i18n.language}
                            addNewAccountCardDocument={addNewAccountCardDocument}
                            showAllAccountCards={showAllAccountCards}
                            setShowAllAccountCards={setShowAllAccountCards}
                            formattedAccountCardOptions={formattedAccountCardOptions}
                            fieldErrors={fieldErrors}
                            invoiceItems={formData.invoiceItems}
                            setInvoiceItems={(items) => setFormData(prev => ({ ...prev, invoiceItems: items }))}
                            client={client}
                            setClient={setClient}
                            invoiceModalType={invoiceModalType}
                            totalsVisible={totalsVisible}
                            setTotalsVisible={setTotalsVisible}
                            CustomDatePicker={CustomDatePicker}
                            AutocompleteSelect={AutocompleteSelect}
                            allClientItemFieldsFilled={allClientItemFieldsFilled}
                            type={type}
                            totalInvoiceAmount={totalInvoiceAmount}
                            handleGoBack={handleGoBack}
                            isAmountAboveLimit={isAmountAboveLimit}
                            formattedCurrencyOptions={formattedCurrencyOptions}
                            addItemFlags={addItemFlags}
                            clientItems={clientItems}
                            allItemsHaveNoVATCalculation={allItemsHaveNoVATCalculation}
                            itemsDisplayMode={itemsDisplayMode}
                            segmentationProjects={segmentationProjects}
                            selectedSegmentation={selectedSegmentation}
                            onSegmentationChange={setSelectedSegmentation}
                            token={token}
                            refreshSegmentationProjects={refreshSegmentationProjects}
                        />
                    </div>
                </div>

                <div className={`floating-totals-badge ${totalsVisible ? 'expanded' : 'collapsed'}`}
                    style={{
                        position: 'fixed',
                        top: '20%',
                        left: 0,
                        zIndex: 99999,
                        background: '#ffffff',
                        borderTopRightRadius: '16px',
                        borderBottomRightRadius: '16px',
                        boxShadow: '2px 4px 12px rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.3s ease',
                        overflow: 'hidden',
                        width: totalsVisible ? '260px' : '50px',
                        minHeight: '60px',
                        fontFamily: "'Segoe UI', Tahoma, sans-serif",
                        color: '#333',
                        transform: 'translateY(-50%)',
                        pointerEvents: 'auto'
                    }}>
                    <div className="totals-header" onClick={() => setTotalsVisible(!totalsVisible)}>
                        {totalsVisible ? <ChevronLeft /> : <ChevronRight />}
                    </div>

                    {totalsVisible && (
                        <div className="totals-content" dir={direction}>
                            <div className="total-row">
                                <span>{t('TotalInvoiceAmount')}</span>
                                <strong>{beautifyNumber(totalInvoiceAmount)}₪</strong>
                            </div>
                            {!Constants.TaxExemptTypes.includes(client?.caseType) && !formData.forceNoTax && (
                                <>
                                    <div className="total-row">
                                        <span>{t('VATAmount')}</span>
                                        <strong>{beautifyNumber(totalVATAmount)}₪</strong>
                                    </div>
                                    <div className="total-row">
                                        <span>{t('TotalWithoutVAT')}</span>
                                        <strong>{beautifyNumber(totalAmountWithoutVAT)}₪</strong>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>


                {/* Signature Reference Modal */}
                <SignatureReferenceModal
                    isVisible={signatureReferenceInvoiceModalVisible}
                    onClose={handleSignatureReferenceClose}
                    onSave={handleSignatureReferenceSave}
                    invoiceReference={invoiceReference}
                    setInvoiceReference={setInvoiceReference}
                    invoiceSignature={invoiceSignature}
                    setInvoiceSignature={setInvoiceSignature}
                    direction={direction}
                    needSignature={needSignature}
                    needReference={needReference}
                    type={t(type)}
                />

                {/* Draft PDF Modal */}
                <Modal
                    open={draftPdfModalVisible}
                    onClose={handleCloseDraftPdfModal}
                    aria-labelledby="draft-invoice-modal-title"
                    aria-describedby="draft-invoice-modal-description"
                >
                    <div className="draft-pdf-modal-container">
                        {/* PDF preview */}
                        {draftPdfBlob && (
                            <iframe
                                src={URL.createObjectURL(draftPdfBlob)}
                                width="100%"
                                height="100%"
                                style={{ border: 'none' }}
                                aria-label="Draft Invoice PDF"
                            />
                        )}

                        {/* Buttons */}
                        <div className="draft-pdf-button-container">
                            {!!client?.email && (
                                <label className="draft-send-to-creator">
                                    <input
                                        type="checkbox"
                                        checked={!!formData.sendToCreator}
                                        onChange={(e) => setFormData(prev => ({ ...prev, sendToCreator: e.target.checked }))}
                                    />
                                    <span className="draft-send-to-creator-text">
                                        <span className="draft-send-to-creator-title">{t('SendToCreator')}</span>
                                        <span className="draft-send-to-creator-email">{client.email}</span>
                                    </span>
                                </label>
                            )}
                            <button
                                className={`create-invoice-button ${!allClientItemFieldsFilled ? 'button-disabled' : ''}`}
                                onClick={allClientItemFieldsFilled ? () => handleCreateInvoice() : null}
                                disabled={!allClientItemFieldsFilled}
                            >
                                <span>{t(type)}</span>
                            </button>

                            <button className="close-invoice-button" onClick={handleCloseDraftPdfModal}>
                                {t('close')}
                            </button>
                        </div>
                    </div>
                </Modal>

                {/*  PDF Modal */}
                <Modal
                    open={PdfModalVisible}
                    onClose={handleClosePdfModal}
                    aria-labelledby="draft-invoice-modal-title"
                    aria-describedby="draft-invoice-modal-description"
                >
                    <div className="draft-pdf-modal-container">
                        {/* PDF preview */}
                        {PdfBlob && (
                            <iframe
                                src={URL.createObjectURL(PdfBlob)}
                                width="100%"
                                height="100%"
                                style={{ border: 'none' }}
                                aria-label="Draft Invoice PDF"
                            />
                        )}

                        {/* Buttons */}
                        <div className="draft-pdf-button-container">


                            <button className="close-invoice-button" onClick={handleClosePdfModal}>
                                {t('close')}
                            </button>
                        </div>
                    </div>
                </Modal>

                {/* Tax Authority Error Modal */}
                <TaxAuthorityErrorModal
                    open={isTaxAuthorityErrorModalVisible}
                    onClose={() => {
                        setIsTaxAuthorityErrorModalVisible(false);
                        setResolvePendingMode(false);
                        setPendingDecisionStatus(null);
                    }}
                    onOptionSelect={handleTaxAuthorityOptionSelect}
                    invoiceData={{
                        invoiceId: pendingDecisionStatus?.invoiceId || formData.invoiceId || "0"
                    }}
                    caseId={client?.caseId || "0"}
                    token={token}
                    pendingStatus={pendingDecisionStatus}
                    resolvePending={resolvePendingMode}
                    onResolved={handlePendingDecisionResolved}
                />

                <ItemPricePickerDialog
                    open={!!pricePickerState}
                    prices={pricePickerState?.prices || []}
                    itemName={pricePickerState?.itemName || ''}
                    direction={direction}
                    onClose={() => setPricePickerState(null)}
                    onSelect={async (chosen) => {
                        const apply = pricePickerState?.apply;
                        setPricePickerState(null);
                        if (apply) await apply(chosen.amount);
                    }}
                />

            </>
            <ToastContainer />
            {loading && (
                <div className="loader-container">
                    <PuffLoader
                        size={100}        // Larger size for better visibility
                        loading={loading}
                    />
                </div>
            )}
        </>

    );
}

export default CreateInvoice;
